'use client';
import useLocationHash from '@/hooks/useLocationHash';
import { buttonLinkTargetProps } from '@/utils/buttonLinkTarget';
import { cn } from '@/utils/classNames';
import { getColorScheme } from '@/utils/getColorScheme';
import { decodeLogoTextEntities, resolveLogoText } from '@/utils/logoText';
import makeId from '@/utils/makeId';
import { Dialog, Disclosure, Popover, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { usePathname, useRouter } from 'next/navigation';
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button } from './atoms/Button';
import IconValue from './atoms/IconValue';
import { MarkDown } from './atoms/MarkDown';

const isOverflowSchemeValue = (value) =>
    String(value || '')
        .toLowerCase()
        .includes('overflow');

const fallbackFromOverflow = (current) => {
    const v = String(current || '').toLowerCase();
    if (v.startsWith('dark')) return 'dark';
    if (
        v.includes('primary') ||
        v.includes('secondary') ||
        v.includes('gradient')
    ) {
        return v.startsWith('light') ? 'light' : 'dark';
    }
    return 'light';
};

const coerceBoolean = (val) =>
    typeof val === 'string' ? val.toLowerCase().trim() === 'true' : !!val;

const readTextValue = (value) => {
    const raw =
        value && typeof value === 'object' && 'value' in value
            ? value.value
            : value;
    return String(raw ?? '').trim();
};

const preferBannerValue = (value, legacyValue) =>
    readTextValue(value) || readTextValue(legacyValue);

const normalizeAriaLabel = (value) => {
    const raw =
        value && typeof value === 'object' && 'value' in value
            ? value.value
            : value;
    return decodeLogoTextEntities(raw)
        .replace(/<[^>]*>/g, '')
        .replace(/\s+/g, ' ')
        .trim();
};

const REVEAL_HIDE_OFFSET = 120;
// Use separate enter/exit thresholds so resizing the fixed header cannot make
// the scroll position repeatedly cross a single boundary during the animation.
const SHRINK_SCROLL_ENTER = 48;
const SHRINK_SCROLL_EXIT = 4;
const OVERFLOW_COLLAPSE_ENTER = 48;
const OVERFLOW_COLLAPSE_EXIT = 4;
const AFTER_HERO_EXTRA_OFFSET = 1200;
const DESKTOP_DROPDOWN_GAP = 16;
const BANNER_SCROLL_SPEED = 48;
const BANNER_SCROLL_EXIT_CLEARANCE = 48;

const isScrollable = (node) => {
    const ownerWindow =
        node?.ownerDocument?.defaultView ||
        (typeof window !== 'undefined' ? window : null);
    if (!node || !ownerWindow) return false;
    const style = ownerWindow.getComputedStyle(node);
    const overflowY = style.overflowY || style.overflow;
    const canScroll =
        overflowY === 'auto' ||
        overflowY === 'scroll' ||
        overflowY === 'overlay';
    return canScroll && node.scrollHeight > node.clientHeight;
};

const getElementVisualBounds = (element) => {
    if (!element?.getBoundingClientRect) return { height: 0, bottom: 0 };

    const rect = element.getBoundingClientRect();

    return {
        height: Math.max(0, Math.round(rect.height)),
        bottom: Math.max(0, Math.round(rect.bottom)),
    };
};

const getNavVisualBounds = (headerElement, spacerElement = null) => {
    const headerBounds = getElementVisualBounds(headerElement);
    const spacerBounds = getElementVisualBounds(spacerElement);

    if (spacerBounds.bottom > headerBounds.bottom) {
        return {
            height: Math.max(headerBounds.height, spacerBounds.height),
            bottom: spacerBounds.bottom,
        };
    }

    const wrapper = headerElement?.closest?.('.edit-component-wrapper');

    if (!wrapper?.getBoundingClientRect) return headerBounds;

    const wrapperRect = wrapper.getBoundingClientRect();
    const wrapperHeight = Math.max(0, Math.round(wrapperRect.height));
    const wrapperBottom = Math.max(0, Math.round(wrapperRect.bottom));

    if (!wrapperHeight || wrapperBottom <= headerBounds.bottom) {
        return headerBounds;
    }

    return {
        height: Math.max(headerBounds.height, wrapperHeight),
        bottom: wrapperBottom,
    };
};

const collectScrollableAncestors = (startEl) => {
    const nodes = [];
    let el = startEl?.parentElement || null;
    while (el) {
        if (isScrollable(el)) nodes.push(el);
        el = el.parentElement;
    }
    return nodes;
};

const getHashTargetId = (anchor) => {
    const normalized = String(anchor || '').trim();
    if (!normalized) return null;

    if (/^\/+#/.test(normalized)) {
        return makeId(normalized.replace(/^\/+#/, ''));
    }

    if (normalized.startsWith('#')) {
        return makeId(normalized.slice(1));
    }

    return null;
};

function DesktopDropdownDismiss({ open, close, headerRef }) {
    useEffect(() => {
        if (!open || typeof document === 'undefined') return undefined;

        const handlePointerDown = (event) => {
            const target = event.target;
            const header = headerRef.current;
            if (!target || typeof target.closest !== 'function' || !header) {
                return;
            }

            const clickedPanel = target.closest(
                '[data-nav-desktop-dropdown-panel]'
            );
            const clickedTrigger = target.closest(
                '[data-nav-desktop-dropdown-trigger]'
            );

            if (
                (clickedPanel && header.contains(clickedPanel)) ||
                (clickedTrigger && header.contains(clickedTrigger))
            ) {
                return;
            }

            close();
        };

        const ownerDocument = headerRef.current?.ownerDocument || null;
        const documents = new Set([document]);
        if (ownerDocument) documents.add(ownerDocument);

        documents.forEach((doc) => {
            doc.addEventListener('pointerdown', handlePointerDown, true);
        });

        return () => {
            documents.forEach((doc) => {
                doc.removeEventListener('pointerdown', handlePointerDown, true);
            });
        };
    }, [open, close, headerRef]);

    return null;
}

const getDocScrollTop = (doc) => {
    if (!doc) return 0;
    return (
        doc.scrollingElement?.scrollTop ||
        doc.documentElement?.scrollTop ||
        doc.body?.scrollTop ||
        0
    );
};

const applyOverflowLogoSizing = (sizeClasses) => {
    const tokens = String(sizeClasses || '')
        .split(/\s+/)
        .filter(Boolean)
        .filter(
            (t) =>
                !/^max-w-/.test(t) &&
                !/^max-h-/.test(t) &&
                !/^lg:max-w-/.test(t) &&
                !/^lg:max-h-/.test(t) &&
                !/^w-/.test(t) &&
                !/^h-/.test(t)
        );

    const baseTokens =
        tokens.length > 0 ? tokens : ['inline-block', 'py-1', 'lg:mx-0'];

    return [
        ...baseTokens,
        'max-w-[200px]',
        'max-h-[90px]',
        'lg:max-w-[600px]',
        'lg:max-h-[600px]',
    ].join(' ');
};

const applyShrinkLogoSizing = (sizeClasses) => {
    const tokens = String(sizeClasses || '')
        .split(/\s+/)
        .filter(Boolean)
        .filter((t) => !/^max-h-/.test(t) && !/^lg:max-h-/.test(t));

    const baseTokens =
        tokens.length > 0
            ? tokens
            : ['inline-block', 'py-1', 'lg:mx-0', 'max-w-[240px]'];

    return [...baseTokens, 'max-h-[55px]', 'lg:max-h-[75px]'].join(' ');
};

const applyMobileLogoSizing = (sizeClasses, style, { maxW, maxH }) => {
    const rawTokens = String(sizeClasses || '')
        .split(/\s+/)
        .filter(Boolean);
    const tokens = rawTokens.filter(
        (t) => !/^max-w-/.test(t) && !/^max-h-/.test(t)
    );

    const baseTokens =
        tokens.length > 0 ? tokens : ['inline-block', 'py-1', 'lg:mx-0'];
    const nextStyle = { ...(style || {}) };

    if (maxW) nextStyle['--nav-logo-max-w-mobile'] = `${maxW}px`;
    if (maxH) nextStyle['--nav-logo-max-h-mobile'] = `${maxH}px`;

    return {
        className: [
            ...baseTokens,
            maxW ? 'max-w-[var(--nav-logo-max-w-mobile)]' : '',
            maxH ? 'max-h-[var(--nav-logo-max-h-mobile)]' : '',
        ]
            .filter(Boolean)
            .join(' '),
        style: Object.keys(nextStyle).length > 0 ? nextStyle : undefined,
    };
};

const NAV_LOGO_SIZE_PRESETS = {
    slim: { maxH: 60 },
    compact: { maxH: 70 },
    standard: { lgMaxW: 285, lgMaxH: 86 },
    large: { lgMaxW: 300, lgMaxH: 100 },
};

const NAV_SERVICE_MEDIA_TOP_PX = {
    slim: 96,
    compact: 108,
    standard: 132,
    large: 156,
};

const NAV_SERVICE_MEDIA_SHIFT_PX = {
    slim: -40,
    compact: -28,
    standard: -4,
    large: 20,
};

const NAV_LOGO_IMAGE_DIMENSIONS = {
    width: 320,
    height: 120,
};

const MOBILE_NAV_LOGO_IMAGE_DIMENSIONS = {
    width: 220,
    height: 64,
};

const firstNonEmptyString = (...values) => {
    for (const value of values) {
        const normalized = String(value || '').trim();
        if (normalized) return normalized;
    }

    return '';
};

const normalizeLogoTextFont = (value) =>
    String(value || '')
        .replace(/["'`<>]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const normalizeLogoTextSize = (value) => {
    const raw = String(value || '').trim();
    const match = raw.match(/^(\d+(?:\.\d+)?)px$/i);
    if (!match) return '';
    const px = Math.max(16, Math.min(48, Number(match[1])));
    return `${px}px`;
};

const normalizeLogoTextWeight = (value) => {
    const parsed = Number.parseInt(String(value || '').trim(), 10);
    if (!Number.isFinite(parsed)) return '';
    return String(Math.max(100, Math.min(1000, Math.round(parsed))));
};

const parsePxValue = (value) => {
    const match = String(value || '')
        .trim()
        .match(/^(\d+(?:\.\d+)?)px$/i);
    return match ? Math.round(Number(match[1])) : null;
};

const getTokenPxValue = (className, prefixes) => {
    const tokens = String(className || '')
        .split(/\s+/)
        .filter(Boolean);

    for (const prefix of prefixes) {
        const token = tokens.find((item) => item.startsWith(prefix));
        if (!token) continue;
        const match = token.match(/\[(\d+)px\]/);
        if (match) return parseInt(match[1], 10);
    }

    return null;
};

const getResponsiveLogoSizes = (className, style) => {
    const desktopWidth =
        parsePxValue(style?.['--nav-logo-max-w-lg']) ||
        getTokenPxValue(className, ['lg:max-w-']) ||
        getTokenPxValue(className, ['max-w-']) ||
        NAV_LOGO_IMAGE_DIMENSIONS.width;
    const mobileWidth =
        parsePxValue(style?.['--nav-logo-max-w-mobile']) ||
        getTokenPxValue(className, ['max-w-']) ||
        Math.min(desktopWidth, 220);

    return `(min-width: 1024px) ${desktopWidth}px, ${mobileWidth}px`;
};

const NavLogoImage = ({ src, alt, className, style, width, height, sizes }) => {
    if (!src) return null;

    return (
        <img
            className={cn('shrink-0', className)}
            style={style}
            src={src}
            alt={alt}
            width={width}
            height={height}
            decoding="async"
            sizes={sizes}
        />
    );
};

const applyLogoSizePreset = (sizeClasses, preset, strategy = 'max') => {
    const rawTokens = String(sizeClasses || '')
        .split(/\s+/)
        .filter(Boolean);

    let lgMaxW = null;
    let lgMaxH = null;

    const tokens = [];
    for (const token of rawTokens) {
        const lgMaxWMatch = token.match(/^lg:max-w-\[(\d+)px\]$/);
        if (lgMaxWMatch) {
            lgMaxW = parseInt(lgMaxWMatch[1], 10);
            continue;
        }

        const lgMaxHMatch = token.match(/^lg:max-h-\[(\d+)px\]$/);
        if (lgMaxHMatch) {
            lgMaxH = parseInt(lgMaxHMatch[1], 10);
            continue;
        }

        if (/^lg:max-w-/.test(token) || /^lg:max-h-/.test(token)) continue;

        tokens.push(token);
    }

    const baseTokens =
        tokens.length > 0 ? tokens : ['inline-block', 'py-1', 'lg:mx-0'];

    const presetLgMaxW = preset?.lgMaxW || 0;
    const presetLgMaxH = preset?.lgMaxH || 0;
    const nextLgMaxW =
        strategy === 'set'
            ? presetLgMaxW || lgMaxW || 0
            : Math.max(lgMaxW || 0, presetLgMaxW);
    const nextLgMaxH =
        strategy === 'set'
            ? presetLgMaxH || lgMaxH || 0
            : Math.max(lgMaxH || 0, presetLgMaxH);

    const style = {};
    if (nextLgMaxW) style['--nav-logo-max-w-lg'] = `${nextLgMaxW}px`;
    if (nextLgMaxH) style['--nav-logo-max-h-lg'] = `${nextLgMaxH}px`;

    return {
        className: [
            ...baseTokens,
            nextLgMaxW ? 'lg:max-w-[var(--nav-logo-max-w-lg)]' : '',
            nextLgMaxH ? 'lg:max-h-[var(--nav-logo-max-h-lg)]' : '',
        ]
            .filter(Boolean)
            .join(' '),
        style: Object.keys(style).length > 0 ? style : undefined,
    };
};

const applyStandardLogoSizing = (sizeClasses) =>
    applyLogoSizePreset(sizeClasses, NAV_LOGO_SIZE_PRESETS.standard, 'set');

const applyLargeLogoSizing = (sizeClasses) =>
    applyLogoSizePreset(sizeClasses, NAV_LOGO_SIZE_PRESETS.large, 'set');

const applyCompactLogoSizing = (sizeClasses) => {
    const rawTokens = String(sizeClasses || '')
        .split(/\s+/)
        .filter(Boolean);
    const tokens = rawTokens.filter(
        (t) =>
            !/^max-h-/.test(t) &&
            !/^lg:max-h-/.test(t) &&
            !/^py-/.test(t) &&
            !/^lg:py-/.test(t)
    );

    const baseTokens = tokens.length > 0 ? tokens : ['inline-block', 'lg:mx-0'];
    const maxH = NAV_LOGO_SIZE_PRESETS.compact.maxH;

    return {
        className: [
            ...baseTokens,
            'py-0',
            'max-h-[var(--nav-logo-max-h)]',
        ].join(' '),
        style: {
            '--nav-logo-max-h': `${maxH}px`,
        },
    };
};

const applySlimLogoSizing = (sizeClasses) => {
    const rawTokens = String(sizeClasses || '')
        .split(/\s+/)
        .filter(Boolean);
    const tokens = rawTokens.filter(
        (t) =>
            !/^max-h-/.test(t) &&
            !/^lg:max-h-/.test(t) &&
            !/^py-/.test(t) &&
            !/^lg:py-/.test(t)
    );

    const baseTokens = tokens.length > 0 ? tokens : ['inline-block', 'lg:mx-0'];
    const maxH = NAV_LOGO_SIZE_PRESETS.slim.maxH;

    return {
        className: [
            ...baseTokens,
            'py-0',
            'max-h-[var(--nav-logo-max-h)]',
        ].join(' '),
        style: {
            '--nav-logo-max-h': `${maxH}px`,
        },
    };
};

const findHeroOffset = () => {
    if (typeof window === 'undefined' || typeof document === 'undefined')
        return AFTER_HERO_EXTRA_OFFSET;

    const hero =
        document.querySelector('[data-hero]') ||
        document.querySelector('[data-component="Hero"]') ||
        document.querySelector('[data-section="hero"]') ||
        document.querySelector('section[id*="hero" i]');

    if (!hero) return AFTER_HERO_EXTRA_OFFSET;

    const rect = hero.getBoundingClientRect();
    const absoluteBottom = rect.bottom + window.scrollY;
    return absoluteBottom + AFTER_HERO_EXTRA_OFFSET;
};

const normalizeBehavior = (value) => {
    const raw = String(value || '')
        .toLowerCase()
        .trim()
        .replace(/[_\s]+/g, '-');
    switch (raw) {
        case 'static':
            return 'static';
        case 'reveal':
        case 'reveal-up':
        case 'reveal-on-scroll-up':
        case 'reveal-on-scroll':
        case 'reveal-scroll-up':
        case 'reveal-scroll':
            return 'sticky'; // reveal behavior removed; fall back to sticky
        case 'sticky-after-hero':
        case 'after-hero':
            return 'sticky-after-hero';
        case 'show-after-hero':
        case 'hidden-until-after-hero':
            return 'show-after-hero';
        case 'shrink':
            return 'shrink';
        case 'sticky':
        default:
            return 'sticky';
    }
};

const normalizeNavHeight = (value) => {
    const raw = String(value || '')
        .toLowerCase()
        .trim()
        .replace(/[_\s]+/g, '-');
    switch (raw) {
        case 'slim':
        case 'tight':
        case 'thin':
        case 'minimal':
        case 'xs':
        case 'x-small':
            return 'slim';
        case 'large':
        case 'taller':
        case 'extra-tall':
        case 'x-tall':
        case 'xl':
        case 'bigger':
        case 'big':
            return 'large';
        case 'standard':
        case 'tall':
            return 'standard';
        case 'compact':
        case 'default':
        case 'normal':
        case 'small':
        case 'sm':
        default:
            return 'compact';
    }
};

function ScrollingBannerMessage({ children }) {
    const viewportRef = useRef(null);
    const messageRef = useRef(null);
    const dragRef = useRef({
        pointerId: null,
        startX: 0,
        startTime: 0,
    });
    const suppressClickRef = useRef(false);
    const [isPaused, setIsPaused] = useState(false);
    const [isButtonHovered, setIsButtonHovered] = useState(false);
    const [metrics, setMetrics] = useState({
        start: null,
        width: null,
        duration: null,
    });

    useLayoutEffect(() => {
        const viewport = viewportRef.current;
        const message = messageRef.current;
        if (!viewport || !message) return undefined;

        const measure = () => {
            const viewportWidth = viewport.clientWidth;
            const messageRect = message.getBoundingClientRect();
            const descendantRightEdge = Array.from(
                message.querySelectorAll('*')
            ).reduce(
                (rightEdge, element) =>
                    Math.max(
                        rightEdge,
                        element.getBoundingClientRect().right - messageRect.left
                    ),
                0
            );
            const messageWidth = Math.ceil(
                Math.max(
                    message.scrollWidth,
                    messageRect.width,
                    descendantRightEdge
                )
            );
            if (!viewportWidth || !messageWidth) return;

            const travelDistance = viewportWidth + messageWidth;
            const duration = Math.max(1, travelDistance / BANNER_SCROLL_SPEED);
            setMetrics((previous) => {
                const next = {
                    start: viewportWidth,
                    width: messageWidth,
                    duration,
                };
                return previous.start === next.start &&
                    previous.width === next.width &&
                    previous.duration === next.duration
                    ? previous
                    : next;
            });
        };

        measure();
        const observer =
            typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(measure)
                : null;
        observer?.observe(viewport);
        observer?.observe(message);
        window.addEventListener('resize', measure);

        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [children]);

    const isMeasured =
        Number.isFinite(metrics.start) &&
        Number.isFinite(metrics.width) &&
        Number.isFinite(metrics.duration);
    const animationStyle = {
        '--banner-scroll-start': `${metrics.start}px`,
        '--banner-scroll-duration': `${metrics.duration || 1}s`,
        '--banner-scroll-exit-clearance': `${BANNER_SCROLL_EXIT_CLEARANCE}px`,
        width: isMeasured ? `${metrics.width}px` : undefined,
        visibility: isMeasured ? undefined : 'hidden',
    };

    const getScrollAnimation = () =>
        messageRef.current
            ?.getAnimations?.()
            .find(
                (animation) =>
                    animation.animationName === 'nav-banner-scroll-loop'
            ) || messageRef.current?.getAnimations?.()[0];

    const resumeAfterDrag = (event) => {
        if (dragRef.current.pointerId !== event.pointerId) return;

        dragRef.current.pointerId = null;
        if (!isButtonHovered) getScrollAnimation()?.play();
        setIsPaused(false);

        if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
    };

    return (
        <div
            ref={viewportRef}
            className="nav-banner-scroll-viewport text-sm leading-6"
            onPointerOver={(event) => {
                if (!event.target.closest?.('.banner-action-buttons')) return;
                getScrollAnimation()?.pause();
                setIsButtonHovered(true);
            }}
            onPointerOut={(event) => {
                const buttonGroup = event.target.closest?.(
                    '.banner-action-buttons'
                );
                if (!buttonGroup) return;
                if (
                    event.relatedTarget?.closest?.('.banner-action-buttons') ===
                    buttonGroup
                ) {
                    return;
                }

                setIsButtonHovered(false);
                if (!isPaused) getScrollAnimation()?.play();
            }}
            onPointerDown={(event) => {
                if (event.button !== 0) return;

                const animation = getScrollAnimation();
                animation?.pause();
                dragRef.current = {
                    pointerId: event.pointerId,
                    startX: event.clientX,
                    startTime: Number(animation?.currentTime) || 0,
                };
                suppressClickRef.current = false;
                event.currentTarget.setPointerCapture?.(event.pointerId);
                setIsPaused(true);
            }}
            onPointerMove={(event) => {
                const drag = dragRef.current;
                if (drag.pointerId !== event.pointerId) return;

                const deltaX = event.clientX - drag.startX;
                if (Math.abs(deltaX) > 3) suppressClickRef.current = true;

                const animation = getScrollAnimation();
                if (!animation) return;

                const durationMs = Math.max(0, metrics.duration * 1000);
                animation.currentTime = Math.min(
                    durationMs,
                    Math.max(
                        0,
                        drag.startTime - (deltaX / BANNER_SCROLL_SPEED) * 1000
                    )
                );
            }}
            onPointerUp={resumeAfterDrag}
            onPointerCancel={resumeAfterDrag}
            onLostPointerCapture={(event) => {
                if (dragRef.current.pointerId !== event.pointerId) return;
                dragRef.current.pointerId = null;
                if (!isButtonHovered) getScrollAnimation()?.play();
                setIsPaused(false);
            }}
            onClickCapture={(event) => {
                if (!suppressClickRef.current) return;
                event.preventDefault();
                event.stopPropagation();
                suppressClickRef.current = false;
            }}
        >
            <div
                ref={messageRef}
                className={`nav-banner-scroll-message ${
                    isMeasured ? 'nav-banner-scroll-message--loop' : ''
                } ${
                    isPaused || isButtonHovered
                        ? 'nav-banner-scroll-message--paused'
                        : ''
                }`}
                style={animationStyle}
            >
                {children}
                <span
                    className="nav-banner-scroll-exit-clearance"
                    aria-hidden="true"
                />
            </div>
        </div>
    );
}

function Banner({
    BannerText,
    BannerTitle,
    BannerAlignment = 'center',
    BannerCTA,
    BannerCTALink,
    BannerCTAOpenInNewTab,
    BannerCTAIcon,
    BannerSecondaryButtonText,
    BannerSecondaryButtonLink,
    BannerSecondaryButtonOpenInNewTab,
    BannerSecondaryButtonIcon,
    BannerOutlineButtonText,
    BannerOutlineButtonLink,
    BannerOutlineButtonOpenInNewTab,
    BannerOutlineButtonIcon,
    // Kept as fallbacks so banners saved before the CTA fields were added
    // continue to render unchanged after the Nav update is deployed.
    BannerButtonText,
    BannerButtonLink,
    BannerButtonIcon,
    BannerColorScheme = 'light',
    BannerGradientDirection = 90,
}) {
    let bannerBG;
    switch (BannerColorScheme) {
        case 'dark':
            bannerBG = 'bg-neutral-800 text-white';
            break;
        case 'light-gradient':
            bannerBG =
                'bg-gradient-to-r from-primary-100 to-secondary-100 text-neutral-800';
            break;
        case 'mid-gradient':
            bannerBG =
                'bg-gradient-to-r from-primary-500 to-secondary-500 text-neutral-800';
            break;
        case 'gradient':
            bannerBG =
                'bg-gradient-to-r from-primary-700 to-secondary-700 text-white';
            break;
        case 'dark-gradient':
            bannerBG =
                'bg-gradient-to-r from-primary-900 to-secondary-900 text-white';
            break;

        case 'light-primary':
            bannerBG = 'bg-primary-100 text-neutral-800';
            break;
        case 'light-secondary':
            bannerBG = 'bg-secondary-100 text-neutral-800';
            break;
        case 'dark-primary':
            bannerBG = 'bg-primary-900 text-white';
            break;
        case 'dark-secondary':
            bannerBG = 'bg-secondary-900 text-white';
            break;
        default:
            bannerBG = 'bg-neutral-100 text-neutral-800';
            break;
    }
    const {
        backgroundStyle: bannerGradientStyle,
        thirdButton: bannerOutlineButtonVariant = 'outline',
    } = getColorScheme(BannerColorScheme, BannerGradientDirection);

    const bannerCTA = preferBannerValue(BannerCTA, BannerButtonText);
    const bannerCTALink = preferBannerValue(BannerCTALink, BannerButtonLink);
    const bannerCTAIcon = preferBannerValue(BannerCTAIcon, BannerButtonIcon);
    const bannerSecondaryButtonText = readTextValue(BannerSecondaryButtonText);
    const bannerSecondaryButtonLink = readTextValue(BannerSecondaryButtonLink);
    const bannerSecondaryButtonIcon = readTextValue(BannerSecondaryButtonIcon);
    const bannerOutlineButtonText = readTextValue(BannerOutlineButtonText);
    const bannerOutlineButtonLink = readTextValue(BannerOutlineButtonLink);
    const bannerOutlineButtonIcon = readTextValue(BannerOutlineButtonIcon);
    const normalizedAlignment = String(BannerAlignment || 'center')
        .trim()
        .toLowerCase();
    const alignmentClasses =
        normalizedAlignment === 'left'
            ? 'items-start text-left sm:items-center sm:justify-start'
            : normalizedAlignment === 'right'
            ? 'items-end text-right sm:items-center sm:justify-end'
            : 'items-center text-center sm:justify-center';
    const buttonAlignmentClasses =
        normalizedAlignment === 'left'
            ? 'justify-start'
            : normalizedAlignment === 'right'
            ? 'justify-end'
            : 'justify-center';
    const isScrollingText = normalizedAlignment === 'scroll';
    const usesBannerEdgeAlignment =
        isScrollingText ||
        normalizedAlignment === 'left' ||
        normalizedAlignment === 'right';
    const bannerContentWidthClasses = isScrollingText
        ? '-mx-2 w-[calc(100%+1rem)] max-w-none'
        : usesBannerEdgeAlignment
        ? 'mx-auto w-full max-w-none'
        : 'mx-auto w-full max-w-7xl';
    const BannerContentWrapper = isScrollingText
        ? ScrollingBannerMessage
        : Fragment;

    const truncateText = (text, maxLength) =>
        text
            ? text.length > maxLength
                ? text.substring(0, maxLength) + '...'
                : text
            : '';

    return (
        <div
            className={`relative isolate z-50 flex w-full overflow-hidden ${bannerBG} mx-auto py-2 xl:py-2 ${
                usesBannerEdgeAlignment ? 'px-2' : 'px-3 xl:px-4'
            }`}
            style={bannerGradientStyle}
        >
            <div
                className="absolute left-[max(-7rem,calc(50%-52rem))] top-1/2 -z-10 -translate-y-1/2 transform-gpu blur-2xl"
                aria-hidden="true"
            />
            <div
                className="left=[max(45rem,calc(50%+8rem))] absolute top-1/2 -z-10 -translate-y-1/2 transform-gpu blur-2xl"
                aria-hidden="true"
            />

            <div
                className={`flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-x-4 ${bannerContentWidthClasses} ${alignmentClasses}`}
            >
                <BannerContentWrapper>
                    <div className="text-sm leading-6">
                        <strong className="inline font-semibold">
                            {isScrollingText
                                ? readTextValue(BannerTitle)
                                : truncateText(BannerTitle, 50)}
                        </strong>
                        {BannerTitle && BannerText && (
                            <svg
                                viewBox="0 0 2 2"
                                className="mx-2 inline h-0.5 w-0.5 fill-current"
                                aria-hidden="true"
                            >
                                <circle cx={1} cy={1} r={1} />
                            </svg>
                        )}
                        <MarkDown
                            wrapperClassName="inline"
                            components={{
                                p: ({ children }) => <span>{children}</span>,
                            }}
                        >
                            {readTextValue(BannerText)}
                        </MarkDown>
                    </div>
                    <div
                        className={`banner-action-buttons flex flex-wrap items-center gap-2 ${buttonAlignmentClasses}`}
                    >
                        {bannerCTA &&
                            (bannerCTALink ? (
                                <Button
                                    asChild
                                    variant="primary"
                                    size="xs"
                                    iconDefault={bannerCTAIcon}
                                    iconVisible={!!bannerCTAIcon}
                                    className="m-0 min-w-fit rounded-[var(--shape-radius)] px-2.5 py-1 text-sm leading-5 shadow-none"
                                >
                                    <a
                                        href={bannerCTALink}
                                        {...buttonLinkTargetProps(
                                            coerceBoolean(BannerCTAOpenInNewTab)
                                        )}
                                    >
                                        {bannerCTA}
                                    </a>
                                </Button>
                            ) : (
                                <Button
                                    variant="primary"
                                    size="xs"
                                    iconDefault={bannerCTAIcon}
                                    iconVisible={!!bannerCTAIcon}
                                    type="button"
                                    className="nj-lead m-0 min-w-fit rounded-[var(--shape-radius)] px-2.5 py-1 text-sm leading-5 shadow-none"
                                >
                                    {bannerCTA}
                                </Button>
                            ))}
                        {bannerSecondaryButtonText &&
                            (bannerSecondaryButtonLink ? (
                                <Button
                                    asChild
                                    variant="secondary"
                                    size="xs"
                                    iconDefault={bannerSecondaryButtonIcon}
                                    iconVisible={!!bannerSecondaryButtonIcon}
                                    className="m-0 min-w-fit rounded-[var(--shape-radius)] px-2.5 py-1 text-sm leading-5 shadow-none"
                                >
                                    <a
                                        href={bannerSecondaryButtonLink}
                                        {...buttonLinkTargetProps(
                                            coerceBoolean(
                                                BannerSecondaryButtonOpenInNewTab
                                            )
                                        )}
                                    >
                                        {bannerSecondaryButtonText}
                                    </a>
                                </Button>
                            ) : (
                                <Button
                                    variant="secondary"
                                    size="xs"
                                    iconDefault={bannerSecondaryButtonIcon}
                                    iconVisible={!!bannerSecondaryButtonIcon}
                                    type="button"
                                    className="m-0 min-w-fit rounded-[var(--shape-radius)] px-2.5 py-1 text-sm leading-5 shadow-none"
                                >
                                    {bannerSecondaryButtonText}
                                </Button>
                            ))}
                        {bannerOutlineButtonText &&
                            (bannerOutlineButtonLink ? (
                                <Button
                                    asChild
                                    variant={bannerOutlineButtonVariant}
                                    size="xs"
                                    iconDefault={bannerOutlineButtonIcon}
                                    iconVisible={!!bannerOutlineButtonIcon}
                                    className="banner-outline-button m-0 min-w-fit rounded-[var(--shape-radius)] px-2.5 py-1 text-sm leading-5 shadow-none"
                                >
                                    <a
                                        href={bannerOutlineButtonLink}
                                        {...buttonLinkTargetProps(
                                            coerceBoolean(
                                                BannerOutlineButtonOpenInNewTab
                                            )
                                        )}
                                    >
                                        {bannerOutlineButtonText}
                                    </a>
                                </Button>
                            ) : (
                                <Button
                                    variant={bannerOutlineButtonVariant}
                                    size="xs"
                                    iconDefault={bannerOutlineButtonIcon}
                                    iconVisible={!!bannerOutlineButtonIcon}
                                    type="button"
                                    className="banner-outline-button m-0 min-w-fit rounded-[var(--shape-radius)] px-2.5 py-1 text-sm leading-5 shadow-none"
                                >
                                    {bannerOutlineButtonText}
                                </Button>
                            ))}
                    </div>
                </BannerContentWrapper>
            </div>
        </div>
    );
}

export default function Nav(props) {
    const {
        ServicesHeader,
        Services,
        Dropdown = [],
        EndDropdowns = [],
        Phone,
        PhoneIcon,
        ShowPhoneIconDesktop = false,
        PhoneIconPosition = 'before',
        PhoneMobileIcon,
        ShowPhoneNumber = false,
        company,
        CTA,
        CTAIcon,
        CTAIconVisible = true,
        CustomCTALink,
        CTAOpenInNewTab,
        CTAMobileIcon,
        Links,
        ColorScheme = 'light',
        Shadow = 'true',
        NavHeight = 'standard',
        LogoText,
        LogoOverflow,
        Behavior = 'sticky',
        Alignment = 'right',
        OutlineButtonText,
        OutlineButtonLink,
        OutlineButtonOpenInNewTab,
        OutlineButtonIcon,
        OutlineButtonIconVisible = true,
        BannerText,
        BannerTitle,
        BannerAlignment,
        BannerCTA,
        BannerCTALink,
        BannerCTAOpenInNewTab,
        BannerCTAIcon,
        BannerSecondaryButtonText,
        BannerSecondaryButtonLink,
        BannerSecondaryButtonOpenInNewTab,
        BannerSecondaryButtonIcon,
        BannerOutlineButtonText,
        BannerOutlineButtonLink,
        BannerOutlineButtonOpenInNewTab,
        BannerOutlineButtonIcon,
        BannerButtonLink,
        BannerButtonText,
        BannerButtonIcon,
        BannerColorScheme,
        BannerGradientDirection,
        EmbedLink = false,
        is_edit_mode = false,
    } = props;

    // Prefer sitewide setting from globals.json, but keep legacy Nav-level fallback.
    const embedLinkSetting =
        company?.embed_cta_link === undefined
            ? EmbedLink
            : company.embed_cta_link;
    const EMBED = coerceBoolean(embedLinkSetting);
    const isEditMode = coerceBoolean(is_edit_mode);
    const hasBannerContent = [
        BannerTitle,
        BannerText,
        BannerCTA,
        BannerButtonText,
        BannerSecondaryButtonText,
        BannerOutlineButtonText,
    ].some((value) => readTextValue(value));
    const ctaOpenInNewTabEnabled = coerceBoolean(CTAOpenInNewTab);
    const outlineOpenInNewTabEnabled = coerceBoolean(OutlineButtonOpenInNewTab);
    const resolvedCTAIcon = String(
        CTAIcon && typeof CTAIcon === 'object' && 'value' in CTAIcon
            ? CTAIcon.value
            : CTAIcon || ''
    ).trim();
    const hasExplicitCTAIcon = Object.prototype.hasOwnProperty.call(
        props,
        'CTAIcon'
    );
    const resolvedCTAIconVisible =
        hasExplicitCTAIcon && !resolvedCTAIcon
            ? false
            : coerceBoolean(CTAIconVisible);
    const ctaAriaLabel = normalizeAriaLabel(CTA) || 'Call to action';
    const resolvedOutlineButtonIcon = String(
        OutlineButtonIcon &&
            typeof OutlineButtonIcon === 'object' &&
            'value' in OutlineButtonIcon
            ? OutlineButtonIcon.value
            : OutlineButtonIcon || ''
    ).trim();
    const resolvedOutlineButtonIconVisible = coerceBoolean(
        OutlineButtonIconVisible
    );

    const rawColorScheme = String(ColorScheme ?? '').trim() || 'light';
    const colorSchemeHasOverflow = isOverflowSchemeValue(rawColorScheme);
    const hasLogoOverflowProp = Object.prototype.hasOwnProperty.call(
        props,
        'LogoOverflow'
    );
    const overflowPropValue =
        LogoOverflow &&
        typeof LogoOverflow === 'object' &&
        'value' in LogoOverflow
            ? LogoOverflow.value
            : LogoOverflow;
    const overflowFromProp = coerceBoolean(overflowPropValue);
    const logoOverflowEnabled =
        overflowFromProp || (!hasLogoOverflowProp && colorSchemeHasOverflow);
    const normalizedColorScheme = colorSchemeHasOverflow
        ? fallbackFromOverflow(rawColorScheme)
        : rawColorScheme;
    const isFloatingNavScheme = [
        'float',
        'float-dark',
        'float-blur',
        'float-blur-dark',
    ].includes(normalizedColorScheme);
    const isTransparentScheme =
        typeof normalizedColorScheme === 'string' &&
        normalizedColorScheme.startsWith('transparent');
    const navHeightValue =
        NavHeight && typeof NavHeight === 'object' && 'value' in NavHeight
            ? NavHeight.value
            : NavHeight;
    const navHeightMode = normalizeNavHeight(navHeightValue || 'standard');
    const isSlimNav = navHeightMode === 'slim';
    const behaviorValue =
        Behavior && typeof Behavior === 'object' && 'value' in Behavior
            ? Behavior.value
            : Behavior;
    const requestedBehaviorMode = isTransparentScheme
        ? 'static'
        : normalizeBehavior(behaviorValue || 'sticky');
    const behaviorMode =
        isSlimNav && requestedBehaviorMode === 'shrink'
            ? 'sticky'
            : requestedBehaviorMode;
    const isRevealBehavior = behaviorMode === 'reveal-on-scroll-up';
    const isStickyAfterHero = behaviorMode === 'sticky-after-hero';
    const isShowAfterHero = behaviorMode === 'show-after-hero';
    const isAfterHeroBehavior = isStickyAfterHero || isShowAfterHero;
    const isShrinkBehavior = behaviorMode === 'shrink';
    const showPhoneNumber = String(ShowPhoneNumber) === 'true';
    const showPhoneIconDesktop = coerceBoolean(ShowPhoneIconDesktop);
    const resolvedPhoneIcon = String(
        PhoneIcon && typeof PhoneIcon === 'object' && 'value' in PhoneIcon
            ? PhoneIcon.value
            : PhoneIcon || 'fa-solid fa-phone'
    ).trim();
    const resolvedPhoneMobileIcon = String(
        PhoneMobileIcon &&
            typeof PhoneMobileIcon === 'object' &&
            'value' in PhoneMobileIcon
            ? PhoneMobileIcon.value
            : PhoneMobileIcon || resolvedPhoneIcon || 'fa-solid fa-phone'
    ).trim();
    const resolvedCTAMobileIcon = String(
        CTAMobileIcon &&
            typeof CTAMobileIcon === 'object' &&
            'value' in CTAMobileIcon
            ? CTAMobileIcon.value
            : CTAMobileIcon || ''
    ).trim();
    const resolvedCTAMobileIconClasses = resolvedCTAMobileIcon || '';
    const resolvedPhoneIconClasses =
        resolvedPhoneIcon.length > 0 ? resolvedPhoneIcon : 'fa-solid fa-phone';
    const resolvedPhoneMobileIconClasses =
        resolvedPhoneMobileIcon.length > 0
            ? resolvedPhoneMobileIcon
            : resolvedPhoneIconClasses;
    const resolvedPhoneIconPosition =
        String(
            PhoneIconPosition &&
                typeof PhoneIconPosition === 'object' &&
                'value' in PhoneIconPosition
                ? PhoneIconPosition.value
                : PhoneIconPosition || 'before'
        )
            .trim()
            .toLowerCase() === 'after'
            ? 'right'
            : 'left';
    const phoneIconButtonBaseClass =
        '!inline-flex !items-center !justify-center !leading-none';
    const iconButtonMobileSizeClass =
        '!aspect-square !p-0 !h-[calc(var(--button-md-line-height,1.75rem)*var(--button-font-scale,1)_+_var(--button-md-py,1rem)*2)] !min-h-[calc(var(--button-md-line-height,1.75rem)*var(--button-font-scale,1)_+_var(--button-md-py,1rem)*2)] !w-[calc(var(--button-md-line-height,1.75rem)*var(--button-font-scale,1)_+_var(--button-md-py,1rem)*2)] !min-w-[calc(var(--button-md-line-height,1.75rem)*var(--button-font-scale,1)_+_var(--button-md-py,1rem)*2)]';
    const phoneIconButtonDesktopSizeClass =
        '!aspect-square !p-0 !h-[calc(var(--button-md-line-height,1.75rem)*var(--button-font-scale,1)_+_var(--button-md-py,1rem)*2)] !min-h-[calc(var(--button-md-line-height,1.75rem)*var(--button-font-scale,1)_+_var(--button-md-py,1rem)*2)] !w-[calc(var(--button-md-line-height,1.75rem)*var(--button-font-scale,1)_+_var(--button-md-py,1rem)*2)] !min-w-[calc(var(--button-md-line-height,1.75rem)*var(--button-font-scale,1)_+_var(--button-md-py,1rem)*2)]';
    const phoneIconInnerClass =
        'inline-flex items-center justify-center leading-none';
    const ctaIconClassMobile =
        'fa-fw block text-[length:calc(var(--button-md-font-size,1.125rem)*var(--button-font-scale,1))] leading-none text-current';
    const phoneIconClassDesktop =
        'fa-fw block text-[length:calc(var(--button-md-font-size,1.125rem)*var(--button-font-scale,1))] leading-none text-current';
    const mobileActionCount =
        1 + (resolvedCTAMobileIconClasses ? 1 : 0) + (Phone ? 1 : 0);

    const { company_name, logoAltText } = company || {};
    const resolvedLogoUrl = firstNonEmptyString(
        company?.logoUrl,
        company?.logo_url,
        company?.logo,
        company?.company_logo,
        company?.LogoUrl,
        company?.LogoURL
    );
    const resolvedLogoAltText =
        String(logoAltText || '').trim() || company_name || 'logo';
    const resolvedLogoText = resolveLogoText(company, LogoText);
    const logoTextFontOverride = normalizeLogoTextFont(company?.logoTextFont);
    const logoTextSizeOverride = normalizeLogoTextSize(company?.logoTextSize);
    const logoTextWeightOverride = normalizeLogoTextWeight(
        company?.logoTextFontWeight
    );

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [embedOpen, setEmbedOpen] = useState(false);
    const [embedSrc, setEmbedSrc] = useState('');
    const [navHidden, setNavHidden] = useState(false);
    const [isPastHeroOffset, setIsPastHeroOffset] = useState(false);
    const [isShrunk, setIsShrunk] = useState(false);
    const [headerHeight, setHeaderHeight] = useState(0);
    const [bannerHeight, setBannerHeight] = useState(0);
    const [isAtTop, setIsAtTop] = useState(true);
    const [stickyAfterHeroVisible, setStickyAfterHeroVisible] = useState(true);
    const [stickyAfterHeroShouldAnimate, setStickyAfterHeroShouldAnimate] =
        useState(false);
    const [overflowCollapsed, setOverflowCollapsed] = useState(false);

    const pathname = usePathname() || '/';
    const router = useRouter();
    const locationHash = useLocationHash();

    // ---------- Embed helpers ----------
    const openEmbed = (url) => {
        if (!url) return;
        setEmbedSrc(url);
        setEmbedOpen(true);
    };

    const getNiceJobHrefFromAnchor = (anchorEl) => {
        if (!anchorEl) return null;
        try {
            const href = anchorEl.getAttribute('href') || '';
            if (/^https?:\/\//i.test(href)) return href;
            return null;
        } catch {
            return null;
        }
    };

    const handleCTAClickSmart = (e) => {
        if (ctaOpenInNewTabEnabled) return;
        if (!EMBED) return;
        const siteLink = CustomCTALink || '';
        let linkToUse = siteLink;

        if (!linkToUse) {
            const anchorEl = e.currentTarget;
            const njHref = getNiceJobHrefFromAnchor(anchorEl);
            if (njHref) linkToUse = njHref;
        }

        if (linkToUse) {
            e.preventDefault();
            e.stopPropagation();
            if (
                e.nativeEvent &&
                typeof e.nativeEvent.stopImmediatePropagation === 'function'
            ) {
                e.nativeEvent.stopImmediatePropagation();
            }
            openEmbed(linkToUse);
        }
    };

    const handleOutlineClick = (e) => {
        if (!EMBED || !OutlineButtonLink || outlineOpenInNewTabEnabled) return;
        e.preventDefault();
        e.stopPropagation();
        if (
            e.nativeEvent &&
            typeof e.nativeEvent.stopImmediatePropagation === 'function'
        ) {
            e.nativeEvent.stopImmediatePropagation();
        }
        openEmbed(OutlineButtonLink);
    };

    // ---------- Smooth scrolling + body lock ----------
    useEffect(() => {
        if (mobileMenuOpen) setMobileMenuOpen(false);
    }, [locationHash]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const navWindow = getNavWindow();
        const targetId = getHashTargetId(
            navWindow?.location?.hash || window.location.hash
        );
        if ((pathname === '/' || isEditMode) && targetId) {
            setTimeout(() => scrollToTargetId(targetId), 100);
        }
    }, [pathname, isEditMode]);

    const getProcessedHref = (anchor) => {
        if (!anchor) return '#';

        const hashTargetId = getHashTargetId(anchor);
        if (hashTargetId) return `/#${hashTargetId}`;

        return anchor;
    };

    useEffect(() => {
        if (isEditMode || isFloatingNavScheme) return;
        if (typeof document === 'undefined') return;

        if (mobileMenuOpen) {
            const scrollY = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
        } else {
            const scrollY = document.body.style.top;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
            if (scrollY) window.scrollTo(0, parseInt(scrollY || '0') * -1);
        }

        return () => {
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
        };
    }, [mobileMenuOpen, isEditMode, isFloatingNavScheme]);

    const lastScrollYRef = useRef(0);
    const heroOffsetRef = useRef(120);
    const navHiddenRef = useRef(false);
    const shrinkRef = useRef(false);
    const pastHeroRef = useRef(false);
    const overflowCollapsedRef = useRef(false);
    const headerRef = useRef(null);
    const spacerRef = useRef(null);
    const bannerRef = useRef(null);
    const scrollSentinelRef = useRef(null);
    const resizeObserverRef = useRef(null);
    const bannerResizeObserverRef = useRef(null);
    const expandedHeaderHeightRef = useRef(0);

    function getNavDocument() {
        return (
            headerRef.current?.ownerDocument ||
            (typeof document !== 'undefined' ? document : null)
        );
    }

    function getNavWindow() {
        const navDocument = getNavDocument();
        return (
            navDocument?.defaultView ||
            (typeof window !== 'undefined' ? window : null)
        );
    }

    function findHashTargetElement(targetId) {
        if (!targetId) return null;

        const documents = new Set();
        const navDocument = getNavDocument();
        if (navDocument) documents.add(navDocument);
        if (typeof document !== 'undefined') documents.add(document);

        for (const doc of documents) {
            const targetElement = doc?.getElementById?.(targetId);
            if (targetElement) return targetElement;
        }

        return null;
    }

    function getAnchorScrollElement(targetElement) {
        if (!targetElement) return null;

        const componentElement = targetElement.closest?.('.editable-component');
        if (!componentElement || componentElement === targetElement) {
            return targetElement;
        }

        const ownerWindow = targetElement.ownerDocument?.defaultView || window;
        const style = ownerWindow.getComputedStyle(targetElement);
        const rect = targetElement.getBoundingClientRect();
        const isHiddenAnchorMarker =
            style.position === 'absolute' &&
            rect.height <= 1 &&
            targetElement.children.length === 0;

        return isHiddenAnchorMarker ? componentElement : targetElement;
    }

    function dispatchHashChange(targetWindow, oldURL, newURL) {
        if (!targetWindow?.dispatchEvent) return;

        try {
            if (typeof targetWindow.HashChangeEvent === 'function') {
                targetWindow.dispatchEvent(
                    new targetWindow.HashChangeEvent('hashchange', {
                        oldURL,
                        newURL,
                    })
                );
                return;
            }
        } catch {
            // Fall back to a plain event below.
        }

        try {
            targetWindow.dispatchEvent(new Event('hashchange'));
        } catch {
            // Ignore event dispatch failures in restricted preview contexts.
        }
    }

    function scrollToTargetId(targetId, options = {}) {
        if (typeof window === 'undefined') return false;

        const targetElement = findHashTargetElement(targetId);
        if (!targetElement) return false;

        targetElement.offsetHeight;
        const scrollElement = getAnchorScrollElement(targetElement);
        if (!scrollElement) return false;

        const targetDocument = scrollElement.ownerDocument || getNavDocument();
        const targetWindow =
            targetDocument?.defaultView ||
            (typeof window !== 'undefined' ? window : null);
        if (!targetDocument || !targetWindow) return false;

        const navElement =
            headerRef.current?.ownerDocument === targetDocument
                ? headerRef.current
                : targetDocument.querySelector('[data-component="Nav"]');
        const spacerElement =
            spacerRef.current?.ownerDocument === targetDocument
                ? spacerRef.current
                : null;
        const headerHeight =
            getNavVisualBounds(navElement, spacerElement).height ||
            navElement?.offsetHeight ||
            100;
        const offset = isFloatingNavScheme ? 0 : headerHeight;
        const scrollContainer = collectScrollableAncestors(scrollElement).find(
            (node) =>
                node !== targetDocument.body &&
                node !== targetDocument.documentElement &&
                node !== targetDocument.scrollingElement
        );

        if (scrollContainer) {
            const targetRect = scrollElement.getBoundingClientRect();
            const containerRect = scrollContainer.getBoundingClientRect();
            scrollContainer.scrollTo({
                top:
                    scrollContainer.scrollTop +
                    targetRect.top -
                    containerRect.top -
                    offset,
                behavior: 'smooth',
            });
        } else {
            const rect = scrollElement.getBoundingClientRect();
            const currentScrollTop =
                targetWindow.pageYOffset || getDocScrollTop(targetDocument);

            targetWindow.scrollTo({
                top: currentScrollTop + rect.top - offset,
                behavior: 'smooth',
            });
        }

        if (options.updateHash !== false) {
            const historyWindow = isEditMode ? window : targetWindow;
            window.setTimeout(() => {
                try {
                    const oldURL = historyWindow.location.href;
                    const { pathname: nextPathname, search } =
                        historyWindow.location;
                    const newURL = `${nextPathname}${search || ''}#${targetId}`;
                    historyWindow.history.replaceState(null, '', newURL);
                    if (oldURL !== historyWindow.location.href) {
                        dispatchHashChange(
                            historyWindow,
                            oldURL,
                            historyWindow.location.href
                        );
                    }
                } catch {
                    try {
                        targetWindow.location.hash = targetId;
                    } catch {
                        // Ignore history updates when the preview window blocks them.
                    }
                }
            }, 500);
        }

        return true;
    }

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const parentWindow =
            window.parent && window.parent !== window
                ? (() => {
                      try {
                          // Accessing parent.document is only allowed same-origin.
                          const parentDoc = window.parent.document;
                          return parentDoc ? window.parent : null;
                      } catch {
                          return null;
                      }
                  })()
                : null;

        heroOffsetRef.current = findHeroOffset();

        const getScrollY = () => {
            let y = 0;
            if (typeof window !== 'undefined') {
                y =
                    window.scrollY ||
                    window.pageYOffset ||
                    getDocScrollTop(document) ||
                    0;
            }

            const ancestors = collectScrollableAncestors(headerRef.current);
            ancestors.forEach((node) => {
                if (typeof node.scrollTop === 'number') {
                    y = Math.max(y, node.scrollTop);
                }
            });

            // If inside a same-origin iframe, include parent scroll position.
            if (parentWindow) {
                const parentDoc = parentWindow.document || null;
                y = Math.max(
                    y,
                    parentWindow.scrollY ||
                        parentWindow.pageYOffset ||
                        getDocScrollTop(parentDoc)
                );
            }

            // Local sentinel fallback (works in nested scroll containers)
            const sentinelRect =
                scrollSentinelRef.current?.getBoundingClientRect();
            if (sentinelRect && typeof sentinelRect.top === 'number') {
                y = Math.max(y, Math.abs(sentinelRect.top));
            }

            // Fallback for transformed/virtual scroll containers (e.g., editors)
            if (typeof document !== 'undefined') {
                const bodyTop = Math.abs(
                    document.body?.getBoundingClientRect()?.top || 0
                );
                const docTop = Math.abs(
                    document.documentElement?.getBoundingClientRect()?.top || 0
                );
                y = Math.max(y, bodyTop, docTop);
            }

            return y;
        };

        const handleScroll = () => {
            const y = getScrollY();
            const lastY = lastScrollYRef.current;
            const topThreshold = Math.max(
                1,
                bannerHeight ? bannerHeight * 0.6 : 20
            );
            const atTopNow = y <= topThreshold;
            if (atTopNow !== isAtTop) setIsAtTop(atTopNow);

            if (isRevealBehavior) {
                const shouldHide = y > REVEAL_HIDE_OFFSET && y > lastY + 2; // scrolling down past threshold
                const shouldShow = y <= REVEAL_HIDE_OFFSET || y < lastY - 2; // scrolling up or above threshold

                if (shouldHide && !navHiddenRef.current) {
                    navHiddenRef.current = true;
                    setNavHidden(true);
                } else if (shouldShow && navHiddenRef.current) {
                    navHiddenRef.current = false;
                    setNavHidden(false);
                }
            } else if (navHiddenRef.current) {
                navHiddenRef.current = false;
                setNavHidden(false);
            }

            if (isAfterHeroBehavior) {
                const pastHero = y >= heroOffsetRef.current;
                if (pastHero !== pastHeroRef.current) {
                    pastHeroRef.current = pastHero;
                    setIsPastHeroOffset(pastHero);
                }
            } else if (pastHeroRef.current) {
                pastHeroRef.current = false;
                setIsPastHeroOffset(false);
            }

            if (isShrinkBehavior) {
                const shrinkNow = shrinkRef.current
                    ? y > SHRINK_SCROLL_EXIT
                    : y >= SHRINK_SCROLL_ENTER;
                if (shrinkNow !== shrinkRef.current) {
                    shrinkRef.current = shrinkNow;
                    setIsShrunk(shrinkNow);
                }
            } else if (shrinkRef.current) {
                shrinkRef.current = false;
                setIsShrunk(false);
            }

            if (logoOverflowEnabled) {
                const collapseOverflow = overflowCollapsedRef.current
                    ? y > OVERFLOW_COLLAPSE_EXIT
                    : y >= OVERFLOW_COLLAPSE_ENTER;
                if (collapseOverflow !== overflowCollapsedRef.current) {
                    overflowCollapsedRef.current = collapseOverflow;
                    setOverflowCollapsed(collapseOverflow);
                }
            } else if (overflowCollapsedRef.current) {
                overflowCollapsedRef.current = false;
                setOverflowCollapsed(false);
            }

            lastScrollYRef.current = y;
        };

        const onScroll = () => {
            handleScroll();
        };

        const onResize = () => {
            heroOffsetRef.current = findHeroOffset();
        };

        handleScroll();

        const scrollTargets = new Set();
        scrollTargets.add(window);
        if (typeof document !== 'undefined') {
            if (document.documentElement)
                scrollTargets.add(document.documentElement);
            if (document.body) scrollTargets.add(document.body);
            if (document.scrollingElement)
                scrollTargets.add(document.scrollingElement);
        }

        collectScrollableAncestors(headerRef.current).forEach((node) =>
            scrollTargets.add(node)
        );
        // If inside a same-origin iframe, also listen to the parent window.
        if (parentWindow) {
            scrollTargets.add(parentWindow);
            const parentDoc = parentWindow.document;
            if (parentDoc?.documentElement)
                scrollTargets.add(parentDoc.documentElement);
            if (parentDoc?.body) scrollTargets.add(parentDoc.body);
            if (parentDoc?.scrollingElement)
                scrollTargets.add(parentDoc.scrollingElement);
        }

        scrollTargets.forEach((target) => {
            try {
                target?.addEventListener('scroll', onScroll, {
                    passive: true,
                });
            } catch {
                // Ignore inaccessible cross-origin targets.
            }
        });
        window.addEventListener('resize', onResize);
        const pollId = window.setInterval(handleScroll, 200);

        return () => {
            scrollTargets.forEach((target) => {
                try {
                    target?.removeEventListener('scroll', onScroll);
                } catch {
                    // Ignore inaccessible cross-origin targets.
                }
            });
            window.removeEventListener('resize', onResize);
            window.clearInterval(pollId);
        };
    }, [behaviorMode, logoOverflowEnabled, bannerHeight, isAtTop]);

    useEffect(() => {
        if (isRevealBehavior) {
            navHiddenRef.current = false;
            setNavHidden(false);
            if (typeof window !== 'undefined') {
                lastScrollYRef.current = window.scrollY || 0;
            }
        }
    }, [isRevealBehavior]);

    useEffect(() => {
        if (mobileMenuOpen && navHiddenRef.current) {
            navHiddenRef.current = false;
            setNavHidden(false);
        }
        if (!isRevealBehavior && navHiddenRef.current) {
            navHiddenRef.current = false;
            setNavHidden(false);
        }
        if (!isShrinkBehavior && shrinkRef.current) {
            shrinkRef.current = false;
            setIsShrunk(false);
        }
    }, [mobileMenuOpen, isRevealBehavior, isShrinkBehavior]);

    useEffect(() => {
        if (!isAfterHeroBehavior && pastHeroRef.current) {
            pastHeroRef.current = false;
            setIsPastHeroOffset(false);
        }
    }, [isAfterHeroBehavior]);

    useLayoutEffect(() => {
        if (!isAfterHeroBehavior || !isPastHeroOffset) {
            setStickyAfterHeroShouldAnimate(false);
            setStickyAfterHeroVisible(true);
            return;
        }

        setStickyAfterHeroShouldAnimate(true);
        setStickyAfterHeroVisible(false);
        if (typeof window === 'undefined') return;

        let raf1 = null;
        let raf2 = null;
        raf1 = window.requestAnimationFrame(() => {
            raf2 = window.requestAnimationFrame(() => {
                setStickyAfterHeroVisible(true);
            });
        });

        return () => {
            if (raf1) window.cancelAnimationFrame(raf1);
            if (raf2) window.cancelAnimationFrame(raf2);
        };
    }, [isAfterHeroBehavior, isPastHeroOffset]);

    useEffect(() => {
        if (!stickyAfterHeroShouldAnimate) return undefined;

        const timeoutId = window.setTimeout(() => {
            setStickyAfterHeroShouldAnimate(false);
        }, 1100);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [stickyAfterHeroShouldAnimate]);

    useEffect(() => {
        if (!logoOverflowEnabled && overflowCollapsedRef.current) {
            overflowCollapsedRef.current = false;
            setOverflowCollapsed(false);
        }
    }, [logoOverflowEnabled]);

    useLayoutEffect(() => {
        if (typeof window === 'undefined' || !headerRef.current) return;

        const measure = () => {
            const measuredHeight = getElementVisualBounds(
                headerRef.current
            ).height;

            // Keep the document spacer at the expanded height while the fixed
            // nav contracts. Otherwise every ResizeObserver frame shortens the
            // document, changes scrollY, and can bounce the shrink state near
            // the top of the page.
            const isCompactHeader =
                (isShrinkBehavior && shrinkRef.current) ||
                (logoOverflowEnabled && overflowCollapsedRef.current);
            if ((isShrinkBehavior || logoOverflowEnabled) && !isCompactHeader) {
                expandedHeaderHeightRef.current = measuredHeight;
            }

            // The root nav metrics have their own ResizeObserver below. Avoid
            // driving a full React render for every interpolated logo height.
            if (!isCompactHeader) {
                setHeaderHeight(measuredHeight);
            }
        };

        measure();
        const frame = window.requestAnimationFrame(measure);
        window.addEventListener('resize', measure);

        if (typeof ResizeObserver !== 'undefined') {
            resizeObserverRef.current = new ResizeObserver(measure);
            resizeObserverRef.current.observe(headerRef.current);
        }

        return () => {
            window.cancelAnimationFrame(frame);
            window.removeEventListener('resize', measure);
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
                resizeObserverRef.current = null;
            }
        };
    }, [
        isShrinkBehavior,
        isShrunk,
        logoOverflowEnabled,
        overflowCollapsed,
        behaviorMode,
        navHeightMode,
    ]);

    useEffect(() => {
        if (typeof window === 'undefined' || !bannerRef.current) {
            setBannerHeight(0);
            return;
        }

        const measureBanner = () => {
            setBannerHeight(bannerRef.current?.offsetHeight || 0);
        };

        measureBanner();

        if (typeof ResizeObserver !== 'undefined') {
            bannerResizeObserverRef.current = new ResizeObserver(measureBanner);
            bannerResizeObserverRef.current.observe(bannerRef.current);
        }

        window.addEventListener('resize', measureBanner);

        return () => {
            window.removeEventListener('resize', measureBanner);
            if (bannerResizeObserverRef.current) {
                bannerResizeObserverRef.current.disconnect();
                bannerResizeObserverRef.current = null;
            }
        };
    }, [hasBannerContent]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            lastScrollYRef.current = window.scrollY || 0;
        }
    }, []);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            lastScrollYRef.current = window.scrollY || 0;
        }
    }, [behaviorMode]);

    const handleServiceNavigation = (e, anchor, isMobile = false) => {
        const targetId = getHashTargetId(anchor);
        if (!targetId) return;

        const isHomePage = pathname === '/';
        const targetHref = `/#${targetId}`;
        const targetElement = findHashTargetElement(targetId);

        if (isMobile && mobileMenuOpen) setMobileMenuOpen(false);

        if (!targetElement) {
            if (!isHomePage && !isEditMode) {
                e.preventDefault();
                router.push(targetHref);
            }
            return;
        }

        e.preventDefault();

        const scrollToSection = () => {
            const didScroll = scrollToTargetId(targetId);
            if (!didScroll && !isEditMode) {
                router.push(targetHref);
            }
        };

        isMobile
            ? setTimeout(scrollToSection, 350)
            : requestAnimationFrame(scrollToSection);
    };

    let linkColor,
        linkHoverColor,
        dropdownBgColor,
        mobileDropdownBgColor,
        dropdownItemColor,
        dropdownItemHoverColor,
        navButtonColor,
        backgroundColor,
        navSticky,
        logoSize,
        logoImgStyle,
        navPadding,
        logoTextColor,
        thirdButton,
        logoOverflow,
        navMargin,
        logoTextSize;

    switch (normalizedColorScheme) {
        case 'dark':
            backgroundColor = 'bg-neutral-800';
            linkColor = 'text-white current:text-primary-300';
            linkHoverColor = 'hover:text-neutral-300';
            dropdownBgColor =
                'w-full lg:w-72 bg-neutral-800 mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-white';
            dropdownItemHoverColor = 'hover:bg-neutral-900 hover:text-white';
            navButtonColor = 'text-white';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-white my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outlineDark';
            break;
        case 'primary':
            backgroundColor = 'bg-primary-800';
            linkColor = 'text-white current:text-primary-300';
            linkHoverColor = 'hover:text-primary-300';
            dropdownBgColor =
                'w-full lg:w-72 bg-primary-800 mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-white ';
            dropdownItemHoverColor = 'hover:bg-primary-700 hover:text-white';
            navButtonColor = 'text-white';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-white my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outlineDark';
            break;
        case 'mid-primary':
            backgroundColor = 'bg-primary-500';
            linkColor = 'text-white current:text-primary-100';
            linkHoverColor = 'hover:text-primary-100';
            dropdownBgColor =
                'w-full lg:w-72 bg-primary-500 mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-white ';
            dropdownItemHoverColor = 'hover:bg-primary-600 hover:text-white';
            navButtonColor = 'text-white';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-white my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outlineDark';
            break;
        case 'dark-primary':
            backgroundColor = 'bg-primary-900';
            linkColor = 'text-white current:text-primary-300';
            linkHoverColor = 'hover:text-primary-300';
            dropdownBgColor =
                'w-full lg:w-72 bg-primary-900 mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-white ';
            dropdownItemHoverColor = 'hover:bg-primary-800 hover:text-white';
            navButtonColor = 'text-white';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-white my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outlineDark';
            break;
        case 'darker-primary':
            backgroundColor = 'bg-primary-950';
            linkColor = 'text-white current:text-primary-300';
            linkHoverColor = 'hover:text-primary-300';
            dropdownBgColor =
                'w-full lg:w-72 bg-primary-950 mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-white ';
            dropdownItemHoverColor = 'hover:bg-primary-900 hover:text-white';
            navButtonColor = 'text-white';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-white my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outlineDark';
            break;
        case 'gradient':
            backgroundColor =
                'bg-gradient-to-br from-primary-700 to-secondary-700';
            linkColor = 'text-white current:text-primary-300';
            linkHoverColor = 'hover:text-primary-300';
            dropdownBgColor =
                'w-full lg:w-72 bg-gradient-to-br from-primary-700 to-secondary-700 mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-white ';
            dropdownItemHoverColor = 'hover:bg-white/20  hover:text-white';
            navButtonColor = 'text-white';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-white my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outlineDark';
            break;
        case 'primary-gradient':
            backgroundColor =
                'bg-gradient-to-br from-primary-500 to-primary-700';
            linkColor = 'text-white current:text-primary-100';
            linkHoverColor = 'hover:text-primary-100';
            dropdownBgColor =
                'w-full lg:w-72 bg-gradient-to-br from-primary-500 to-primary-700 mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-white';
            dropdownItemHoverColor = 'hover:bg-white/20 hover:text-white';
            navButtonColor = 'text-white';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-white my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outlineDark';
            break;
        case 'dark-primary-gradient':
            backgroundColor =
                'bg-gradient-to-br from-primary-800 to-primary-950';
            linkColor = 'text-white current:text-primary-300';
            linkHoverColor = 'hover:text-primary-200';
            dropdownBgColor =
                'w-full lg:w-72 bg-gradient-to-br from-primary-800 to-primary-950 mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-white';
            dropdownItemHoverColor = 'hover:bg-white/20 hover:text-white';
            navButtonColor = 'text-white';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-white my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outlineDark';
            break;
        case 'dark-gradient':
            backgroundColor =
                'bg-gradient-to-br from-primary-900 to-secondary-900';
            linkColor = 'text-white current:text-primary-300';
            linkHoverColor = 'hover:text-primary-300';
            dropdownBgColor =
                'w-full lg:w-72 bg-gradient-to-br from-primary-900 to-secondary-900 mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-white ';
            dropdownItemHoverColor = 'hover:bg-white/20  hover:text-white';
            navButtonColor = 'text-white';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-white my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outlineDark';
            break;
        case 'light-gradient':
            backgroundColor =
                'bg-gradient-to-br from-primary-100 to-secondary-100';
            linkColor =
                'text-neutral-800 current:text-neutral-900 hover:text-neutral-900';
            linkHoverColor = 'hover:text-neutral-900';
            dropdownBgColor =
                'w-full lg:w-72 bg-gradient-to-br from-primary-100 to-secondary-100 mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-neutral-800';
            dropdownItemHoverColor = 'hover:bg-white/20  hover:text-white';
            navButtonColor = 'text-neutral-800';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-neutral-800 my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outlineDark';
            break;
        case 'light-primary-gradient':
            backgroundColor =
                'bg-gradient-to-br from-primary-200 to-primary-400';
            linkColor =
                'text-neutral-800 current:text-primary-900 hover:text-primary-900';
            linkHoverColor = 'hover:text-primary-900';
            dropdownBgColor =
                'w-full lg:w-72 bg-gradient-to-br from-primary-200 to-primary-400 mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-neutral-800';
            dropdownItemHoverColor = 'hover:bg-white/20  hover:text-white';
            navButtonColor = 'text-neutral-800';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-neutral-800 my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outlineDark';
            break;
        case 'light-secondary-gradient':
            backgroundColor =
                'bg-gradient-to-br from-secondary-200 to-secondary-400';
            linkColor =
                'text-neutral-800 current:text-secondary-900 hover:text-secondary-900';
            linkHoverColor = 'hover:text-secondary-900';
            dropdownBgColor =
                'w-full lg:w-72 bg-gradient-to-br from-secondary-200 to-secondary-400 mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-neutral-800';
            dropdownItemHoverColor = 'hover:bg-white/20  hover:text-white';
            navButtonColor = 'text-neutral-800';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-neutral-800 my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outlineDark';
            break;
        case 'mid-gradient':
            backgroundColor =
                'bg-gradient-to-br from-primary-500 to-secondary-500';
            linkColor = 'text-white current:text-white hover:text-white';
            linkHoverColor = 'hover:text-white';
            dropdownBgColor =
                'w-full lg:w-72 bg-gradient-to-br from-primary-500 to-secondary-500 mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-white';
            dropdownItemHoverColor = 'hover:bg-white/20 hover:text-white';
            navButtonColor = 'text-white';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-white my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outlineDark';
            break;
        case 'secondary-gradient':
            backgroundColor =
                'bg-gradient-to-br from-secondary-500 to-secondary-700';
            linkColor = 'text-white current:text-secondary-100';
            linkHoverColor = 'hover:text-secondary-100';
            dropdownBgColor =
                'w-full lg:w-72 bg-gradient-to-br from-secondary-500 to-secondary-700 mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-white';
            dropdownItemHoverColor = 'hover:bg-white/20  hover:text-white';
            navButtonColor = 'text-white';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-white my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outlineDark';
            break;
        case 'dark-secondary-gradient':
            backgroundColor =
                'bg-gradient-to-br from-secondary-800 to-secondary-950';
            linkColor = 'text-white current:text-secondary-300';
            linkHoverColor = 'hover:text-secondary-200';
            dropdownBgColor =
                'w-full lg:w-72 bg-gradient-to-br from-secondary-800 to-secondary-950 mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-white';
            dropdownItemHoverColor = 'hover:bg-white/20  hover:text-white';
            navButtonColor = 'text-white';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-white my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outlineDark';
            break;
        case 'secondary':
            backgroundColor = 'bg-secondary-800';
            linkColor = 'text-white current:text-secondary-300';
            linkHoverColor = 'hover:text-secondary-300';
            dropdownBgColor =
                'w-full lg:w-72 bg-secondary-800 mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-white ';
            dropdownItemHoverColor = 'hover:bg-secondary-700 hover:text-white';
            navButtonColor = 'text-white';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-white my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outlineDark';
            break;
        case 'dark-secondary':
            backgroundColor = 'bg-secondary-900';
            linkColor = 'text-white current:text-secondary-300';
            linkHoverColor = 'hover:text-secondary-300';
            dropdownBgColor =
                'w-full lg:w-72 bg-secondary-900 mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-white ';
            dropdownItemHoverColor = 'hover:bg-secondary-800 hover:text-white';
            navButtonColor = 'text-white';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-white my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outlineDark';
            break;
        case 'darker-secondary':
            backgroundColor = 'bg-secondary-950';
            linkColor = 'text-white current:text-secondary-300';
            linkHoverColor = 'hover:text-secondary-300';
            dropdownBgColor =
                'w-full lg:w-72 bg-secondary-950 mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-white ';
            dropdownItemHoverColor = 'hover:bg-secondary-900 hover:text-white';
            navButtonColor = 'text-white';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-white my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outlineDark';
            break;
        case 'dark-overflow':
            backgroundColor = 'bg-neutral-800';
            linkColor = 'text-white current:text-primary-300';
            linkHoverColor = 'hover:text-neutral-300';
            dropdownBgColor =
                'w-full lg:w-72 bg-neutral-800 mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-white ';
            dropdownItemHoverColor = 'hover:bg-neutral-900 hover:text-white';
            navButtonColor = 'text-white';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[180px]';
            logoTextColor = 'text-white my-7 lg:mb-[130px]';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outlineDark';
            logoOverflow = 'lg:mb-[-100px]';
            break;
        case 'transparent-dark':
            backgroundColor = 'bg-neutral-800 lg:bg-transparent';
            linkColor = 'text-white current:text-primary-300';
            linkHoverColor = 'hover:text-neutral-300';
            dropdownBgColor =
                'w-full lg:w-72 bg-neutral-800 mt-20 lg:mt-6 shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-white';
            dropdownItemHoverColor = 'hover:bg-neutral-900 hover:text-white';
            navButtonColor = 'text-white';
            navSticky = 'sticky lg:shadow-none lg:relative';
            navPadding = '-mb-8';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[140px]';
            logoTextColor = 'text-white';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outlineDark';
            break;

        case 'transparent-light':
            backgroundColor = 'bg-white lg:bg-transparent';
            linkColor = 'text-neutral-800 current:text-primary-800';
            linkHoverColor = 'hover:text-neutral-800';
            dropdownBgColor = 'w-full lg:w-72 bg-white mt-20 lg:mt-6 shadow-lg';
            mobileDropdownBgColor = 'bg-neutral-800/5';
            dropdownItemColor = 'text-neutral-800';
            dropdownItemHoverColor =
                'hover:bg-neutral-100 hover:text-neutral-800';
            navButtonColor = 'text-neutral-800';
            navSticky = 'sticky lg:shadow-none lg:relative';
            navPadding = '-mb-8';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[140px]';
            logoTextColor = 'text-primary-800';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outline';
            break;
        case 'transparent-primary':
            backgroundColor = 'bg-primary-800 lg:bg-transparent';
            linkColor = 'text-white current:text-primary-300';
            linkHoverColor = 'hover:text-primary-300';
            dropdownBgColor =
                'w-full lg:w-72 bg-primary-800 mt-20 lg:mt-6 shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-white';
            dropdownItemHoverColor = 'hover:bg-primary-800 hover:text-white';
            navButtonColor = 'text-white';
            navSticky = 'sticky lg:shadow-none lg:relative';
            navPadding = '-mb-8';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[140px]';
            logoTextColor = 'text-white';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outlineDark';
            break;

        case 'transparent-secondary':
            backgroundColor = 'bg-secondary-800 lg:bg-transparent';
            linkColor = 'text-white current:text-secondary-300';
            linkHoverColor = 'hover:text-secondary-300';
            dropdownBgColor =
                'w-full lg:w-72 bg-secondary-800 mt-20 lg:mt-6 shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-white';
            dropdownItemHoverColor = 'hover:bg-secondary-800 hover:text-white';
            navButtonColor = 'text-white';
            navSticky = 'sticky lg:shadow-none lg:relative';
            navPadding = '-mb-8';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[140px]';
            logoTextColor = 'text-white';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outlineDark';
            break;

        case 'light-primary':
            backgroundColor = 'bg-primary-100';
            linkColor = 'text-neutral-800 current:text-primary-800';
            linkHoverColor = 'hover:text-primary-800';
            dropdownBgColor =
                'w-full lg:w-72 bg-primary-100 mt-[85px] lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-primary-100';
            dropdownItemColor = 'text-neutral-800';
            dropdownItemHoverColor =
                'hover:bg-primary-200 hover:text-neutral-800';
            navButtonColor = 'text-neutral-800';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-primary-800 my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outline';
            break;
        case 'lighter-primary':
            backgroundColor = 'bg-primary-50';
            linkColor = 'text-neutral-800 current:text-primary-800';
            linkHoverColor = 'hover:text-primary-800';
            dropdownBgColor =
                'w-full lg:w-72 bg-primary-100 mt-[85px] lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-primary-50';
            dropdownItemColor = 'text-neutral-800';
            dropdownItemHoverColor =
                'hover:bg-primary-100 hover:text-neutral-800';
            navButtonColor = 'text-neutral-800';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-primary-800 my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outline';
            break;
        case 'light-secondary':
            backgroundColor = 'bg-secondary-100';
            linkColor = 'text-neutral-800 current:text-secondary-800';
            linkHoverColor = 'hover:text-secondary-800';
            dropdownBgColor =
                'w-full lg:w-72 bg-secondary-100 mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-secondary-100';
            dropdownItemColor = 'text-neutral-800';
            dropdownItemHoverColor =
                'hover:bg-secondary-200 hover:text-neutral-800';
            navButtonColor = 'text-neutral-800';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-secondary-800 my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outline';
            break;
        case 'mid-secondary':
            backgroundColor = 'bg-secondary-500';
            linkColor = 'text-white current:text-secondary-100';
            linkHoverColor = 'hover:text-secondary-100';
            dropdownBgColor =
                'w-full lg:w-72 bg-secondary-500 mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-white ';
            dropdownItemHoverColor = 'hover:bg-secondary-600 hover:text-white';
            navButtonColor = 'text-white';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-white my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outlineDark';
            break;
        case 'lighter-secondary':
            backgroundColor = 'bg-secondary-50';
            linkColor = 'text-neutral-800 current:text-secondary-800';
            linkHoverColor = 'hover:text-secondary-800';
            dropdownBgColor =
                'w-full lg:w-72 bg-white mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-secondary-50';
            dropdownItemColor = 'text-neutral-800';
            dropdownItemHoverColor =
                'hover:bg-neutral-50 hover:text-neutral-800';
            navButtonColor = 'text-neutral-800';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-secondary-800 my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outline';
            break;
        case 'light-overflow':
            backgroundColor = ' bg-white';
            linkColor = 'text-neutral-800 current:text-primary-800';
            linkHoverColor = 'hover:text-primary-800';
            dropdownBgColor =
                'w-full lg:w-72 bg-white mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'bg-neutral-800/5';
            dropdownItemColor = 'text-neutral-800';
            dropdownItemHoverColor =
                'hover:bg-neutral-100 hover:text-neutral-800';
            navButtonColor = 'text-neutral-800';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[180px]';
            logoTextColor = 'text-primary-800 my-7 lg:mb-[130px]';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outline';
            logoOverflow = 'lg:mb-[-100px]';
            break;
        case 'float-dark':
            backgroundColor = 'bg-neutral-800';
            linkColor = 'text-white current:text-primary-300';
            linkHoverColor = 'hover:text-neutral-300';
            dropdownBgColor =
                ' sm:max-w[300px] bg-neutral-800 lg:mt-12 min-w-[300px] md:w-full m-4 mt-32 rounded-[min(1.5rem,var(--shape-radius))] shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-white';
            dropdownItemHoverColor = 'hover:bg-neutral-900';
            navButtonColor = 'text-white';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[240px] max-h-[80px] lg:max-h-[115px]';
            logoTextColor = 'text-white my-3';
            logoTextSize = 'text-base lg:text-2xl';
            navMargin =
                ' lg:top-12 top-4 lg:mt-10 mt-4 mx-4 lg:mx-auto lg:w-[1000px] xl:w-[1200px] p-2 rounded-[var(--shape-radius)]';
            thirdButton = 'outlineDark';
            break;
        case 'float':
            backgroundColor = 'bg-white';
            linkColor = 'text-neutral-800 current:text-primary-800';
            linkHoverColor = 'hover:text-primary-800';
            dropdownBgColor =
                ' sm:max-w[300px] bg-white lg:mt-12 min-w-[300px] md:w-full m-4 mt-32 rounded-[min(1.5rem,var(--shape-radius))] shadow-lg';
            mobileDropdownBgColor = 'bg-neutral-800/5';
            dropdownItemColor = 'text-neutral-800';
            dropdownItemHoverColor =
                'hover:bg-neutral-100 hover:text-neutral-800';
            navButtonColor = 'text-neutral-800';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[240px] max-h-[80px] lg:max-h-[115px]';
            logoTextColor = 'text-primary-700 my-3';
            logoTextSize = 'text-base lg:text-2xl';
            navMargin =
                ' lg:top-12 top-4 lg:mt-10 mt-4 mx-4 lg:mx-auto lg:w-[1000px] xl:w-[1200px] p-2 rounded-[var(--shape-radius)]';
            thirdButton = 'outline';
            break;
        case 'float-blur':
            backgroundColor =
                'bg-white bg-opacity-70 bg-blur backdrop-blur-md backdrop-filter';
            linkColor = 'text-neutral-800 current:text-primary-800';
            linkHoverColor = 'hover:text-primary-800';
            dropdownBgColor =
                ' sm:max-w[300px] bg-white lg:mt-12 min-w-[300px] md:w-full m-4 mt-32 rounded-[min(1.5rem,var(--shape-radius))] shadow-lg';
            mobileDropdownBgColor = 'bg-neutral-800/5';
            dropdownItemColor = 'text-neutral-800';
            dropdownItemHoverColor =
                'hover:bg-neutral-100 hover:text-neutral-800';
            navButtonColor = 'text-neutral-800';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[240px] max-h-[80px] lg:max-h-[115px]';
            logoTextColor = 'text-primary-700 my-3';
            logoTextSize = 'text-base lg:text-2xl';
            navMargin =
                ' lg:top-12 top-4 lg:mt-10 mt-4 mx-4 lg:mx-auto lg:w-[1000px] xl:w-[1200px] p-2 rounded-[var(--shape-radius)]';
            thirdButton = 'outline';
            break;
        case 'float-blur-dark':
            backgroundColor =
                'bg-black bg-opacity-70 bg-blur backdrop-blur-md backdrop-filter';
            linkColor = 'text-white current:text-primary-300';
            linkHoverColor = 'hover:text-neutral-300';
            dropdownBgColor =
                'sm:max-w[300px] bg-neutral-800 lg:mt-12 min-w-[300px] md:w-full m-4 mt-32 rounded-[min(1.5rem,var(--shape-radius))] shadow-lg';
            mobileDropdownBgColor = 'bg-white/5';
            dropdownItemColor = 'text-white';
            dropdownItemHoverColor = 'hover:bg-neutral-900';
            navButtonColor = 'text-white';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[240px] max-h-[80px] lg:max-h-[115px]';
            logoTextColor = 'text-white my-3';
            logoTextSize = 'text-base lg:text-2xl';
            navMargin =
                ' lg:top-12 top-4 lg:mt-10 mt-4 mx-4 lg:mx-auto lg:w-[1000px] xl:w-[1200px] p-2 rounded-[var(--shape-radius)]';
            thirdButton = 'outlineDark';
            break;

        default: // light
            backgroundColor = ' bg-white';
            linkColor = 'text-neutral-800 current:text-primary-700';
            linkHoverColor = 'hover:text-primary-700';
            dropdownBgColor =
                'w-full lg:w-72 bg-white mt-20 lg:mt-12 shadow-lg';
            mobileDropdownBgColor = 'text-neutral-800 bg-neutral-800/5';
            dropdownItemColor = 'text-neutral-800';
            dropdownItemHoverColor = 'hover:bg-neutral-100';
            navButtonColor = 'text-neutral-800';
            navSticky = 'sticky';
            logoSize =
                'inline-block py-1 lg:mx-0 max-w-[275px] max-h-[85px] lg:max-h-[125px]';
            logoTextColor = 'text-primary-800 my-7';
            logoTextSize = 'text-base lg:text-2xl';
            thirdButton = 'outline';
            break;
    }

    const POSITION_TOKEN = /(^|:)(sticky|relative|fixed)$/;
    const stickyTokens = String(navSticky || 'sticky')
        .split(/\s+/)
        .filter(Boolean);
    const nonPositionTokens = stickyTokens.filter(
        (t) => !POSITION_TOKEN.test(t)
    );
    const behaviorPositionClass = (() => {
        switch (behaviorMode) {
            case 'static':
                return 'relative';
            case 'reveal-on-scroll-up':
                return isFloatingNavScheme
                    ? 'fixed left-4 right-4 top-0'
                    : 'fixed left-0 right-0 top-0 w-full';
            case 'sticky':
            case 'shrink':
                return !(hasBannerContent && isAtTop)
                    ? isFloatingNavScheme
                        ? 'fixed left-4 right-4 top-0'
                        : 'fixed left-0 right-0 top-0 w-full'
                    : 'relative';
            case 'sticky-after-hero':
                return isPastHeroOffset
                    ? isFloatingNavScheme
                        ? 'fixed left-4 right-4 top-0'
                        : 'fixed left-0 right-0 top-0 w-full'
                    : 'relative';
            case 'show-after-hero':
                return isPastHeroOffset
                    ? isFloatingNavScheme
                        ? 'fixed left-4 right-4 top-0'
                        : 'fixed left-0 right-0 top-0 w-full'
                    : 'relative';
            default:
                return isFloatingNavScheme
                    ? 'fixed left-4 right-4 top-0'
                    : 'fixed left-0 right-0 top-0 w-full';
        }
    })();
    navSticky = nonPositionTokens.join(' ');

    const navMarginTokens = String(navMargin || '')
        .split(/\s+/)
        .filter(Boolean)
        .filter(
            (t) =>
                !/(^|:)top-/.test(t) &&
                !(
                    isFloatingNavScheme &&
                    behaviorPositionClass.includes('fixed') &&
                    /^mx-/.test(t)
                )
        );
    const navMarginClass = navMarginTokens.join(' ');
    const isFixedPosition = behaviorPositionClass.includes('fixed');
    useLayoutEffect(() => {
        if (typeof window === 'undefined') return;

        const root = document.documentElement;
        const serviceMediaTop =
            NAV_SERVICE_MEDIA_TOP_PX[navHeightMode] ||
            NAV_SERVICE_MEDIA_TOP_PX.compact;
        const serviceMediaShift =
            NAV_SERVICE_MEDIA_SHIFT_PX[navHeightMode] ??
            NAV_SERVICE_MEDIA_SHIFT_PX.compact;

        root.dataset.navHeightMode = navHeightMode;
        root.style.setProperty(
            '--nav-service-media-top',
            `${serviceMediaTop}px`
        );
        root.style.setProperty(
            '--nav-service-media-shift',
            `${serviceMediaShift}px`
        );

        return () => {
            delete root.dataset.navHeightMode;
            root.style.removeProperty('--nav-service-media-top');
            root.style.removeProperty('--nav-service-media-shift');
        };
    }, [navHeightMode]);

    useLayoutEffect(() => {
        if (typeof window === 'undefined') return;

        let frame = 0;
        let observer = null;
        const root = document.documentElement;
        const measureNav = () => {
            window.cancelAnimationFrame(frame);
            frame = window.requestAnimationFrame(() => {
                const bounds = getNavVisualBounds(
                    headerRef.current,
                    spacerRef.current
                );
                const height = Math.max(0, bounds.height || headerHeight || 0);
                const visualBottom = bounds.bottom;

                root.style.setProperty('--nav-h', `${height}px`);
                root.style.setProperty('--nav-offset-y', `${visualBottom}px`);
                root.style.setProperty(
                    '--nav-offset-y-portrait-width',
                    `${Math.max(0, visualBottom) * (9 / 16)}px`
                );
            });
        };

        measureNav();
        window.addEventListener('resize', measureNav);
        window.addEventListener('scroll', measureNav, { passive: true });

        if (headerRef.current && typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(measureNav);
            observer.observe(headerRef.current);
        }

        return () => {
            window.cancelAnimationFrame(frame);
            window.removeEventListener('resize', measureNav);
            window.removeEventListener('scroll', measureNav);
            observer?.disconnect();
            root.style.removeProperty('--nav-h');
            root.style.removeProperty('--nav-offset-y');
            root.style.removeProperty('--nav-offset-y-portrait-width');
        };
    }, [
        headerHeight,
        isFixedPosition,
        behaviorMode,
        isShrunk,
        navHeightMode,
        navHidden,
        stickyAfterHeroVisible,
        isPastHeroOffset,
        mobileMenuOpen,
    ]);
    const computeRevealHideDistance = () => {
        const base = headerHeight || REVEAL_HIDE_OFFSET;
        if (typeof window === 'undefined' || !headerRef.current)
            return base + 12;
        const style = window.getComputedStyle(headerRef.current);
        const marginTop = parseFloat(style.marginTop) || 0;
        const marginBottom = parseFloat(style.marginBottom) || 0;
        return base + marginTop + marginBottom + 12;
    };
    const revealHideDistance = computeRevealHideDistance();
    const revealStyle = isRevealBehavior
        ? {
              transform: navHidden
                  ? `translateY(-${revealHideDistance}px)`
                  : 'translateY(0)',
              transition: 'transform 250ms ease',
              willChange: 'transform',
          }
        : {};
    const headerStyle = { ...revealStyle };
    const stickyAfterHeroHiddenTransform = 'translateY(-100%)';
    const stickyAfterHeroAnimationClass =
        isAfterHeroBehavior && isPastHeroOffset && stickyAfterHeroShouldAnimate
            ? 'animate-nav-slide-down'
            : '';
    if (isShowAfterHero) {
        if (mobileMenuOpen) {
            delete headerStyle.transform;
            delete headerStyle.willChange;
            headerStyle.opacity = 1;
            headerStyle.pointerEvents = 'auto';
        } else if (isPastHeroOffset) {
            headerStyle.transform = stickyAfterHeroVisible
                ? 'translateY(0)'
                : stickyAfterHeroHiddenTransform;
            headerStyle.opacity = stickyAfterHeroVisible ? 1 : 0;
            headerStyle.pointerEvents = stickyAfterHeroVisible
                ? 'auto'
                : 'none';
            headerStyle.willChange = 'transform, opacity';
        } else {
            headerStyle.transform = stickyAfterHeroHiddenTransform;
            headerStyle.opacity = 0;
            headerStyle.pointerEvents = 'none';
            headerStyle.willChange = 'transform, opacity';
        }
    }

    if (isStickyAfterHero) {
        if (isPastHeroOffset) {
            headerStyle.transform = stickyAfterHeroVisible
                ? 'translateY(0)'
                : stickyAfterHeroHiddenTransform;
            headerStyle.opacity = stickyAfterHeroVisible ? 1 : 0;
            headerStyle.pointerEvents = stickyAfterHeroVisible
                ? 'auto'
                : 'none';
            headerStyle.willChange = 'transform, opacity';
        } else {
            headerStyle.transform = 'translateY(0)';
            headerStyle.opacity = 1;
            headerStyle.pointerEvents = 'auto';
            delete headerStyle.willChange;
        }
    }

    headerStyle['--nav-open-radius'] = 'min(2.75rem, var(--shape-radius, 0px))';

    const navEditorVisibleState = (() => {
        if (isShowAfterHero) {
            if (mobileMenuOpen) return true;
            if (isPastHeroOffset) return stickyAfterHeroVisible;
            return false;
        }

        if (isStickyAfterHero) {
            return true;
        }

        if (isRevealBehavior) {
            return !navHidden;
        }

        return true;
    })();

    const behaviorHeaderClasses = [];
    if (isRevealBehavior) {
        behaviorHeaderClasses.push(
            'transition-transform duration-300 will-change-transform'
        );
    }
    if (isAfterHeroBehavior && !mobileMenuOpen) {
        behaviorHeaderClasses.push(
            'transition-[transform,opacity] duration-300 will-change-transform'
        );
    }
    behaviorHeaderClasses.push('transition-[top] duration-300');

    if (logoOverflowEnabled && !overflowCollapsed) {
        logoOverflow = 'lg:mb-[-40px]';
        logoSize = applyOverflowLogoSizing(logoSize);
        // ensure natural sizing but avoid forcing giant dimensions on mobile
        logoImgStyle = {
            ...(logoImgStyle || {}),
            width: 'auto',
            height: 'auto',
        };
        if (resolvedLogoText && !/lg:mb-\[60px\]/.test(logoTextColor || '')) {
            logoTextColor = `${logoTextColor} lg:mb-[60px]`;
        }
    }

    const shouldShrinkLogo =
        (isShrinkBehavior && isShrunk) ||
        (logoOverflowEnabled && overflowCollapsed);
    if (shouldShrinkLogo) {
        logoSize = applyShrinkLogoSizing(logoSize);
        if (logoTextSize) {
            logoTextSize = 'text-sm lg:text-xl';
        }
        if (logoOverflow) logoOverflow = '';
    }

    const isStandardNav = navHeightMode === 'standard';
    const isLargeNav = navHeightMode === 'large';
    const isCompactNav = navHeightMode === 'compact';
    if (
        (isCompactNav || isSlimNav || isStandardNav || isLargeNav) &&
        !shouldShrinkLogo
    ) {
        if (!resolvedLogoText) {
            const nextLogo = isCompactNav
                ? applyCompactLogoSizing(logoSize)
                : isSlimNav
                ? applySlimLogoSizing(logoSize)
                : isLargeNav
                ? applyLargeLogoSizing(logoSize)
                : applyStandardLogoSizing(logoSize);
            logoSize = nextLogo.className;
            logoImgStyle = nextLogo.style;
        } else if (logoTextSize && isLargeNav) {
            const nextTokens = String(logoTextSize || '')
                .split(/\s+/)
                .filter(Boolean)
                .filter((t) => !/^lg:text-/.test(t));
            logoTextSize = [...nextTokens, 'lg:text-3xl'].join(' ');
        }
    }

    // The large-nav preset runs after the overflow sizing above. Reapply the
    // expanded overflow dimensions here so the large logo is not reduced back
    // to the normal large-nav limit.
    if (isLargeNav && logoOverflowEnabled && !overflowCollapsed) {
        logoSize = applyOverflowLogoSizing(logoSize);
        logoImgStyle = {
            ...(logoImgStyle || {}),
            width: 'auto',
            height: 'auto',
            '--nav-logo-max-w-lg': '480px',
            // Keep the navigation's layout height at the large preset height;
            // the image itself is allowed to extend beyond this box below.
            '--nav-logo-max-h-lg': '100px',
        };
    }

    if (!resolvedLogoText) {
        const mobileLogoMaxWidth =
            mobileActionCount >= 3 ? 144 : mobileActionCount === 2 ? 176 : 208;
        const mobileLogoMaxHeight = isSlimNav
            ? 52
            : isCompactNav
            ? 56
            : isLargeNav
            ? 64
            : 60;
        const nextLogo = applyMobileLogoSizing(logoSize, logoImgStyle, {
            maxW: mobileLogoMaxWidth,
            maxH: mobileLogoMaxHeight,
        });
        logoSize = nextLogo.className;
        logoImgStyle = nextLogo.style;
    }

    if (resolvedLogoText && logoTextSize) {
        const nextTokens = String(logoTextSize || '')
            .split(/\s+/)
            .filter(Boolean)
            .filter((t) => !/^lg:text-/.test(t) && !/^xl:text-/.test(t));
        logoTextSize = [
            ...nextTokens,
            'lg:[font-size:clamp(1rem,1.8vw,1.45rem)]',
            'xl:[font-size:clamp(1.25rem,1.6vw,1.9rem)]',
        ].join(' ');
    }

    const logoResizeTransitionClass = logoOverflowEnabled
        ? 'duration-500 ease-in-out'
        : 'duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]';
    const logoContainerClass = cn(
        'flex min-w-0 flex-1 items-center overflow-hidden transition-[margin] motion-reduce:transition-none lg:flex-none lg:shrink-0 lg:overflow-visible',
        logoResizeTransitionClass,
        resolvedLogoText ? 'mx-2 md:mx-3' : 'mx-3',
        logoOverflow || ''
    );
    const logoTextContainerClass = cn(
        'font-display font-bold leading-[var(--logo-text-line-height,1)]',
        'transition-[font-size,line-height] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
        'w-auto min-w-[10rem] px-3',
        resolvedLogoText
            ? 'max-w-[18rem] lg:min-w-[14rem] lg:max-w-[22rem] lg:px-2 xl:min-w-[16rem] xl:max-w-[28rem] 2xl:max-w-none 2xl:px-3 [&_p]:mb-0 [&_p]:leading-[var(--logo-text-line-height,0.9)]'
            : 'md:w-72',
        resolvedLogoText ? '' : 'max-w-xs',
        logoTextColor,
        logoTextSize
    );
    const logoTextOverrideStyle =
        logoTextFontOverride || logoTextSizeOverride || logoTextWeightOverride
            ? {
                  ...(logoTextFontOverride
                      ? { fontFamily: `"${logoTextFontOverride}"` }
                      : {}),
                  ...(logoTextSizeOverride
                      ? {
                            fontSize: logoTextSizeOverride,
                            lineHeight: '1.05',
                            '--logo-text-line-height': '1.05',
                        }
                      : {}),
                  ...(logoTextWeightOverride
                      ? { fontWeight: logoTextWeightOverride }
                      : {}),
              }
            : undefined;
    const desktopLogoSizes = getResponsiveLogoSizes(logoSize, logoImgStyle);
    const logoBoxVariables = Object.entries(logoImgStyle || {}).reduce(
        (acc, [key, value]) => {
            if (key.startsWith('--')) acc[key] = value;
            return acc;
        },
        {}
    );
    const isExpandedLargeOverflow =
        isLargeNav && logoOverflowEnabled && !overflowCollapsed;
    const logoBoxStyle = !resolvedLogoText
        ? {
              ...logoBoxVariables,
              '--nav-logo-max-w-lg':
                  logoBoxVariables['--nav-logo-max-w-lg'] ||
                  (isExpandedLargeOverflow ? '360px' : '285px'),
              '--nav-logo-max-h-lg':
                  logoBoxVariables['--nav-logo-max-h-lg'] ||
                  logoBoxVariables['--nav-logo-max-h'] ||
                  (isExpandedLargeOverflow ? '120px' : '86px'),
              '--nav-logo-max-w-mobile':
                  logoBoxVariables['--nav-logo-max-w-mobile'] || '176px',
              '--nav-logo-max-h-mobile':
                  logoBoxVariables['--nav-logo-max-h-mobile'] ||
                  logoBoxVariables['--nav-logo-max-h'] ||
                  '60px',
          }
        : undefined;
    const desktopLogoImageClass = cn(
        'block h-auto w-auto max-h-[var(--nav-logo-max-h-mobile)] max-w-[var(--nav-logo-max-w-mobile)] object-contain object-left transition-[max-height,max-width] motion-reduce:transition-none lg:max-h-[var(--nav-logo-max-h-lg)] lg:max-w-[var(--nav-logo-max-w-lg)]',
        logoResizeTransitionClass,
        isExpandedLargeOverflow ? 'lg:h-auto lg:max-h-[160px]' : ''
    );
    const desktopActionClass = showPhoneNumber
        ? 'md:[--button-md-font-size:1rem] md:[--button-md-line-height:1.5rem] md:[--button-md-px:1rem] md:[--button-md-py:0.875rem]'
        : '';
    const desktopPhoneClass = showPhoneNumber
        ? 'md:[--button-md-font-size:0.9rem] md:[--button-md-line-height:1.5rem] md:[--button-md-px:0.8rem] md:[--button-md-py:0.875rem]'
        : '';

    const isLogoCollapseActive = logoOverflowEnabled && overflowCollapsed;
    const shouldReducePadding =
        (isShrinkBehavior && isShrunk) || isLogoCollapseActive;
    const navItemPaddingClass = shouldReducePadding
        ? 'px-[0.56rem] py-[0.56rem] lg:px-[clamp(0.42rem,0.62vw,0.56rem)] lg:py-[clamp(0.42rem,0.62vw,0.56rem)]'
        : 'px-3 py-3 lg:px-[clamp(0.5rem,0.8vw,0.75rem)] lg:py-[clamp(0.5rem,0.8vw,0.75rem)]';
    const navGroupPaddingY = shouldReducePadding
        ? 'py-[0.95rem]'
        : isSlimNav
        ? 'py-1'
        : isCompactNav
        ? 'py-1.5'
        : isLargeNav
        ? 'py-3'
        : 'py-2';
    const navTextSize =
        isShrinkBehavior && isShrunk
            ? 'text-[0.66rem] lg:[font-size:clamp(0.66rem,0.8vw,0.75rem)]'
            : 'text-sm lg:[font-size:clamp(0.72rem,1.05vw,1rem)]';
    const updateDesktopDropdownPosition = (event) => {
        if (typeof window === 'undefined') return;

        const buttonRect = event.currentTarget?.getBoundingClientRect?.();
        if (!buttonRect) return;

        const headerRect = headerRef.current?.getBoundingClientRect?.() || {
            left: 0,
        };

        headerRef.current?.style.setProperty(
            '--nav-dropdown-left',
            `${Math.round(buttonRect.left - headerRect.left - 12)}px`
        );
    };
    const renderDesktopDropdownPanel = (children, className) => {
        return (
            <Popover.Panel
                data-nav-desktop-dropdown-panel
                className={cn(
                    'absolute left-[var(--nav-dropdown-left,1rem)] z-[80] overflow-hidden rounded-[min(1.5rem,var(--shape-radius))]',
                    shadowBox,
                    className
                )}
                style={{ top: `calc(100% + ${DESKTOP_DROPDOWN_GAP}px)` }}
            >
                {children}
            </Popover.Panel>
        );
    };
    const navPaddingY = shouldReducePadding
        ? 'py-[0.35rem]'
        : isSlimNav
        ? 'py-1'
        : isCompactNav
        ? 'py-1.5'
        : isLargeNav
        ? 'lg:py-3'
        : isStandardNav
        ? 'lg:py-2'
        : '';
    const mobileMenuButtonPadding = isCompactNav
        ? 'p-[1.125rem]'
        : isSlimNav
        ? 'p-4'
        : 'p-5';
    const desktopDropdownSurfaceClass = String(dropdownBgColor || '')
        .split(/\s+/)
        .filter(Boolean)
        .filter(
            (token) =>
                !/(^|:)w-/.test(token) &&
                !/(^|:)min-w-/.test(token) &&
                !/(^|:)max-w/.test(token) &&
                !/(^|:)m[trblxy]?-[^\s]+/.test(token) &&
                !/(^|:)rounded/.test(token)
        )
        .join(' ');
    const mobileMenuPanelClass = String(dropdownBgColor || '')
        .split(/\s+/)
        .filter(Boolean)
        .filter(
            (token) =>
                !/(^|:)w-/.test(token) &&
                !/(^|:)min-w-/.test(token) &&
                !/(^|:)max-w-/.test(token) &&
                !/(^|:)m[trblxy]?-[^\s]+/.test(token) &&
                !/(^|:)rounded/.test(token)
        )
        .join(' ');
    const floatMobileSurfaceToneClass = (() => {
        if (normalizedColorScheme === 'float-blur') {
            return 'bg-neutral-200';
        }

        if (normalizedColorScheme === 'float-blur-dark') {
            return 'bg-neutral-800';
        }

        if (normalizedColorScheme === 'float-dark') {
            return 'bg-neutral-800';
        }

        return 'bg-white';
    })();
    const floatMobileSurfaceClass = cn(
        floatMobileSurfaceToneClass,
        'shadow-none'
    );
    const floatMobileSubmenuSurfaceClass = (() => {
        if (normalizedColorScheme === 'float-blur') {
            return 'bg-neutral-100';
        }

        if (normalizedColorScheme === 'float-blur-dark') {
            return 'bg-neutral-700';
        }

        if (normalizedColorScheme === 'float-dark') {
            return 'bg-neutral-700';
        }

        return 'bg-neutral-50';
    })();
    const headerSurfaceClass =
        isFloatingNavScheme && mobileMenuOpen
            ? floatMobileSurfaceToneClass
            : backgroundColor;
    const mobileMenuSurfaceClass = isFloatingNavScheme
        ? floatMobileSurfaceClass
        : mobileMenuPanelClass;
    const mobileMenuPositionClass = isFloatingNavScheme
        ? 'absolute right-0 top-full z-[51] w-full max-h-[calc(100dvh-var(--nav-offset-y,4rem))] origin-top overflow-y-auto rounded-b-[var(--nav-open-radius)] transform-gpu will-change-transform'
        : 'fixed inset-y-0 right-0 z-[51] w-[75vw] max-w-[20rem] overflow-y-auto';
    const mobileMenuDialogPositionClass = isFloatingNavScheme
        ? 'absolute right-0 top-full z-50 w-full max-h-[calc(100dvh-var(--nav-offset-y,4rem))] origin-top overflow-y-auto rounded-b-[var(--nav-open-radius)] transform-gpu will-change-transform'
        : 'fixed inset-y-0 right-0 z-50 w-[75vw] max-w-[20rem] overflow-y-auto';
    const mobileMenuOverlayClass = isFloatingNavScheme
        ? 'fixed inset-0 z-[50] bg-transparent'
        : 'fixed inset-0 z-40 bg-black/20';
    const mobileMenuTransitionEnter = isFloatingNavScheme
        ? 'transition-[clip-path] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]'
        : 'transition duration-500 ease-out';
    const mobileMenuTransitionEnterFrom = isFloatingNavScheme
        ? '[clip-path:inset(0_0_100%_0)]'
        : 'opacity-0 translate-x-full';
    const mobileMenuTransitionEnterTo = isFloatingNavScheme
        ? '[clip-path:inset(0_0_0_0)]'
        : 'opacity-100 translate-x-0';
    const mobileMenuTransitionLeave = isFloatingNavScheme
        ? 'transition-[clip-path] duration-260 ease-in-out'
        : 'transition ease-in duration-200';
    const mobileMenuTransitionLeaveFrom = isFloatingNavScheme
        ? '[clip-path:inset(0_0_0_0)]'
        : 'opacity-100 translate-x-0';
    const mobileMenuTransitionLeaveTo = isFloatingNavScheme
        ? '[clip-path:inset(0_0_100%_0)]'
        : 'opacity-0 translate-x-full';
    const mobileMenuContentTransitionEnter = isFloatingNavScheme ? '' : '';
    const mobileMenuContentTransitionEnterFrom = isFloatingNavScheme ? '' : '';
    const mobileMenuContentTransitionEnterTo = isFloatingNavScheme ? '' : '';
    const mobileMenuContentTransitionLeave = isFloatingNavScheme ? '' : '';
    const mobileMenuContentTransitionLeaveFrom = isFloatingNavScheme ? '' : '';
    const mobileMenuContentTransitionLeaveTo = isFloatingNavScheme ? '' : '';
    const mobileSubmenuPanelClass = cn(
        'origin-top overflow-hidden',
        isFloatingNavScheme ? 'pt-2 pb-6' : 'py-3',
        isFloatingNavScheme
            ? floatMobileSubmenuSurfaceClass
            : mobileDropdownBgColor
    );
    const mobileSubmenuContentClass = cn('space-y-1', dropdownItemColor);
    const mobileSubmenuTransitionEnter =
        'transition-[clip-path] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]';
    const mobileSubmenuTransitionEnterFrom = '[clip-path:inset(0_0_100%_0)]';
    const mobileSubmenuTransitionEnterTo = '[clip-path:inset(0_0_0_0)]';
    const mobileSubmenuTransitionLeave =
        'transition-[clip-path] duration-260 ease-in-out';
    const mobileSubmenuTransitionLeaveFrom = '[clip-path:inset(0_0_0_0)]';
    const mobileSubmenuTransitionLeaveTo = '[clip-path:inset(0_0_100%_0)]';
    const spacerBaseHeight =
        ((isShrinkBehavior || logoOverflowEnabled) &&
            expandedHeaderHeightRef.current) ||
        headerHeight ||
        REVEAL_HIDE_OFFSET;
    const spacerHeight = (() => {
        if (!isFixedPosition) return 0;
        if (isShowAfterHero && !isPastHeroOffset) return 0;
        if (!isRevealBehavior || !navHidden) return spacerBaseHeight;
        return 0;
    })();

    let navAlign;
    switch (Alignment) {
        case 'left':
            navAlign = 'justify-start';
            break;
        default:
            navAlign = 'justify-end pr-8';
            break;
    }

    const shadowBox = coerceBoolean(Shadow) ? 'shadow' : '';
    const mobileMenuContent = (
        <>
            {!isFloatingNavScheme && (
                <div className="flex items-start justify-between gap-4 px-6 pb-3 pt-6">
                    <a
                        href="/"
                        className="min-w-0 py-1 [-webkit-tap-highlight-color:transparent] focus:outline-none focus-visible:outline-none"
                    >
                        {resolvedLogoText ? (
                            <div
                                data-logo-text="mobile"
                                className={cn(
                                    'block max-w-[13rem] whitespace-nowrap font-display text-2xl font-bold leading-none [&_p]:mb-0 [&_p]:leading-none',
                                    logoTextColor
                                )}
                                style={logoTextOverrideStyle}
                            >
                                <MarkDown>{resolvedLogoText}</MarkDown>
                            </div>
                        ) : (
                            <NavLogoImage
                                className="block h-12 w-auto max-w-[13rem] object-contain object-left"
                                src={resolvedLogoUrl}
                                alt={resolvedLogoAltText}
                                width={MOBILE_NAV_LOGO_IMAGE_DIMENSIONS.width}
                                height={MOBILE_NAV_LOGO_IMAGE_DIMENSIONS.height}
                                sizes="208px"
                            />
                        )}
                    </a>
                </div>
            )}
            <div className="flow-root">
                <div
                    className={cn(
                        'divide-y',
                        isFloatingNavScheme ? '' : '-my-5'
                    )}
                >
                    <div
                        className={cn(
                            'space-y-2 py-6',
                            isFloatingNavScheme && 'pb-6 pt-4'
                        )}
                    >
                        {/* Services (mobile) */}
                        {ServicesHeader && (
                            <Disclosure as="div" className="lg:hidden">
                                {({ open }) => (
                                    <>
                                        <Disclosure.Button
                                            className={`flex w-full items-center justify-between px-6 py-3 text-sm font-semibold ${dropdownItemColor}`}
                                        >
                                            <MarkDown>
                                                {ServicesHeader}
                                            </MarkDown>
                                            <ChevronDownIcon
                                                className={`ml-2 h-5 w-5 flex-none transition-transform ${
                                                    open ? 'rotate-180' : ''
                                                }`}
                                                aria-hidden="true"
                                            />
                                        </Disclosure.Button>
                                        <Transition
                                            show={open}
                                            as={Fragment}
                                            enter={mobileSubmenuTransitionEnter}
                                            enterFrom={
                                                mobileSubmenuTransitionEnterFrom
                                            }
                                            enterTo={
                                                mobileSubmenuTransitionEnterTo
                                            }
                                            leave={mobileSubmenuTransitionLeave}
                                            leaveFrom={
                                                mobileSubmenuTransitionLeaveFrom
                                            }
                                            leaveTo={
                                                mobileSubmenuTransitionLeaveTo
                                            }
                                        >
                                            <Disclosure.Panel
                                                static
                                                className={
                                                    mobileSubmenuPanelClass
                                                }
                                            >
                                                <div
                                                    className={
                                                        mobileSubmenuContentClass
                                                    }
                                                >
                                                    {Services?.map((item) => {
                                                        const href =
                                                            getProcessedHref(
                                                                item.Anchor
                                                            );
                                                        return (
                                                            <a
                                                                key={
                                                                    item.Anchor
                                                                }
                                                                href={href}
                                                                onClick={(e) =>
                                                                    handleServiceNavigation(
                                                                        e,
                                                                        item.Anchor,
                                                                        true
                                                                    )
                                                                }
                                                                className={`block px-6 py-3 text-sm font-semibold ${dropdownItemColor}`}
                                                            >
                                                                <MarkDown>
                                                                    {item.Name}
                                                                </MarkDown>
                                                            </a>
                                                        );
                                                    })}
                                                </div>
                                            </Disclosure.Panel>
                                        </Transition>
                                    </>
                                )}
                            </Disclosure>
                        )}

                        {/* Additional dropdowns (mobile) */}
                        {Dropdown?.map((menu, index) =>
                            menu.Header ? (
                                <Disclosure
                                    as="div"
                                    className="lg:hidden"
                                    key={index}
                                >
                                    {({ open }) => (
                                        <>
                                            <Disclosure.Button
                                                className={`flex w-full items-center justify-between px-6 py-3 text-sm font-semibold ${dropdownItemColor}`}
                                            >
                                                <MarkDown>
                                                    {menu.Header}
                                                </MarkDown>
                                                <ChevronDownIcon
                                                    className={`ml-2 h-5 w-5 flex-none transition-transform ${
                                                        open ? 'rotate-180' : ''
                                                    }`}
                                                    aria-hidden="true"
                                                />
                                            </Disclosure.Button>
                                            <Transition
                                                show={open}
                                                as={Fragment}
                                                enter={
                                                    mobileSubmenuTransitionEnter
                                                }
                                                enterFrom={
                                                    mobileSubmenuTransitionEnterFrom
                                                }
                                                enterTo={
                                                    mobileSubmenuTransitionEnterTo
                                                }
                                                leave={
                                                    mobileSubmenuTransitionLeave
                                                }
                                                leaveFrom={
                                                    mobileSubmenuTransitionLeaveFrom
                                                }
                                                leaveTo={
                                                    mobileSubmenuTransitionLeaveTo
                                                }
                                            >
                                                <Disclosure.Panel
                                                    static
                                                    className={
                                                        mobileSubmenuPanelClass
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            mobileSubmenuContentClass
                                                        }
                                                    >
                                                        {menu.Items?.map(
                                                            (
                                                                item,
                                                                itemIndex
                                                            ) => {
                                                                const href =
                                                                    getProcessedHref(
                                                                        item.Anchor
                                                                    );
                                                                const isHashLink =
                                                                    !!getHashTargetId(
                                                                        item.Anchor
                                                                    );

                                                                return (
                                                                    <a
                                                                        key={
                                                                            itemIndex
                                                                        }
                                                                        href={
                                                                            href
                                                                        }
                                                                        onClick={(
                                                                            e
                                                                        ) => {
                                                                            if (
                                                                                isHashLink
                                                                            ) {
                                                                                handleServiceNavigation(
                                                                                    e,
                                                                                    item.Anchor,
                                                                                    true
                                                                                );
                                                                            }
                                                                        }}
                                                                        className={`block px-6 py-3 text-sm font-semibold ${linkColor}`}
                                                                    >
                                                                        <MarkDown>
                                                                            {
                                                                                item.Name
                                                                            }
                                                                        </MarkDown>
                                                                    </a>
                                                                );
                                                            }
                                                        )}
                                                    </div>
                                                </Disclosure.Panel>
                                            </Transition>
                                        </>
                                    )}
                                </Disclosure>
                            ) : null
                        )}

                        {/* Regular Links (mobile) */}
                        {Links?.map(({ Name, Link }) => {
                            const isHashLink = !!getHashTargetId(Link);
                            const href = getProcessedHref(Link);
                            return (
                                <a
                                    key={Link}
                                    href={href}
                                    onClick={(e) => {
                                        if (isHashLink)
                                            handleServiceNavigation(
                                                e,
                                                Link,
                                                true
                                            );
                                    }}
                                    className={`block px-6 py-2 text-sm font-semibold leading-7 ${linkColor}`}
                                >
                                    <MarkDown>{Name}</MarkDown>
                                </a>
                            );
                        })}

                        {/* End dropdowns (mobile) */}
                        {EndDropdowns?.map((menu, index) => {
                            const endMenuItems = (menu.Items || []).filter(
                                (item) =>
                                    String(item?.Name || '').trim() &&
                                    String(item?.Anchor || '').trim()
                            );
                            if (!menu.Header) return null;
                            return (
                                <Disclosure
                                    as="div"
                                    className="lg:hidden"
                                    key={`end-${index}`}
                                >
                                    {({ open }) => (
                                        <>
                                            <Disclosure.Button
                                                className={`flex w-full items-center justify-between px-6 py-3 text-sm font-semibold ${dropdownItemColor}`}
                                            >
                                                <MarkDown>
                                                    {menu.Header}
                                                </MarkDown>
                                                <ChevronDownIcon
                                                    className={`ml-2 h-5 w-5 flex-none transition-transform ${
                                                        open ? 'rotate-180' : ''
                                                    }`}
                                                    aria-hidden="true"
                                                />
                                            </Disclosure.Button>
                                            <Transition
                                                show={open}
                                                as={Fragment}
                                                enter={
                                                    mobileSubmenuTransitionEnter
                                                }
                                                enterFrom={
                                                    mobileSubmenuTransitionEnterFrom
                                                }
                                                enterTo={
                                                    mobileSubmenuTransitionEnterTo
                                                }
                                                leave={
                                                    mobileSubmenuTransitionLeave
                                                }
                                                leaveFrom={
                                                    mobileSubmenuTransitionLeaveFrom
                                                }
                                                leaveTo={
                                                    mobileSubmenuTransitionLeaveTo
                                                }
                                            >
                                                <Disclosure.Panel
                                                    static
                                                    className={
                                                        mobileSubmenuPanelClass
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            mobileSubmenuContentClass
                                                        }
                                                    >
                                                        {endMenuItems.map(
                                                            (
                                                                item,
                                                                itemIndex
                                                            ) => {
                                                                const href =
                                                                    getProcessedHref(
                                                                        item.Anchor
                                                                    );
                                                                const isHashLink =
                                                                    !!getHashTargetId(
                                                                        item.Anchor
                                                                    );

                                                                return (
                                                                    <a
                                                                        key={
                                                                            itemIndex
                                                                        }
                                                                        href={
                                                                            href
                                                                        }
                                                                        onClick={(
                                                                            e
                                                                        ) => {
                                                                            if (
                                                                                isHashLink
                                                                            ) {
                                                                                handleServiceNavigation(
                                                                                    e,
                                                                                    item.Anchor,
                                                                                    true
                                                                                );
                                                                            }
                                                                        }}
                                                                        className={`block px-6 py-3 text-sm font-semibold ${linkColor}`}
                                                                    >
                                                                        <MarkDown>
                                                                            {
                                                                                item.Name
                                                                            }
                                                                        </MarkDown>
                                                                    </a>
                                                                );
                                                            }
                                                        )}
                                                    </div>
                                                </Disclosure.Panel>
                                            </Transition>
                                        </>
                                    )}
                                </Disclosure>
                            );
                        })}
                    </div>
                </div>
            </div>
        </>
    );

    return (
        <>
            <div ref={scrollSentinelRef} aria-hidden="true" />
            <div ref={bannerRef}>
                {hasBannerContent && (
                    <Banner
                        BannerTitle={BannerTitle}
                        BannerText={BannerText}
                        BannerAlignment={BannerAlignment}
                        BannerCTA={BannerCTA}
                        BannerCTALink={BannerCTALink}
                        BannerCTAOpenInNewTab={BannerCTAOpenInNewTab}
                        BannerCTAIcon={BannerCTAIcon}
                        BannerSecondaryButtonText={BannerSecondaryButtonText}
                        BannerSecondaryButtonLink={BannerSecondaryButtonLink}
                        BannerSecondaryButtonOpenInNewTab={
                            BannerSecondaryButtonOpenInNewTab
                        }
                        BannerSecondaryButtonIcon={BannerSecondaryButtonIcon}
                        BannerOutlineButtonText={BannerOutlineButtonText}
                        BannerOutlineButtonLink={BannerOutlineButtonLink}
                        BannerOutlineButtonOpenInNewTab={
                            BannerOutlineButtonOpenInNewTab
                        }
                        BannerOutlineButtonIcon={BannerOutlineButtonIcon}
                        BannerButtonLink={BannerButtonLink}
                        BannerButtonText={BannerButtonText}
                        BannerButtonIcon={BannerButtonIcon}
                        BannerColorScheme={BannerColorScheme}
                        BannerGradientDirection={BannerGradientDirection}
                    />
                )}
            </div>

            <header
                ref={headerRef}
                data-component="Nav"
                data-nav-height={navHeightMode}
                data-editor-visible={navEditorVisibleState ? 'true' : 'false'}
                className={`top-0 ${
                    isAtTop ? 'z-50' : 'z-[60]'
                } ${headerSurfaceClass} ${
                    navMarginClass || ''
                } ${navSticky} ${shadowBox} ${behaviorHeaderClasses.join(
                    ' '
                )} ${stickyAfterHeroAnimationClass} ${behaviorPositionClass} ${
                    isFloatingNavScheme && mobileMenuOpen
                        ? '!rounded-b-none !rounded-t-[var(--nav-open-radius)]'
                        : ''
                }`}
                style={headerStyle}
            >
                <nav
                    className={`mx-auto flex items-center justify-between px-2 lg:px-4 ${
                        navPadding || ''
                    } ${navPaddingY} ${
                        isShrinkBehavior || isLogoCollapseActive
                            ? `transition-[padding] motion-reduce:transition-none ${logoResizeTransitionClass}`
                            : ''
                    }`}
                >
                    {/* Logo */}
                    <div className={logoContainerClass}>
                        {resolvedLogoText ? (
                            <div
                                data-logo-text="primary"
                                className={logoTextContainerClass}
                                style={logoTextOverrideStyle}
                            >
                                <a
                                    href="/"
                                    aria-label={
                                        normalizeAriaLabel(resolvedLogoText) ||
                                        company_name ||
                                        'logo'
                                    }
                                >
                                    <MarkDown>{resolvedLogoText}</MarkDown>
                                </a>
                            </div>
                        ) : resolvedLogoUrl ? (
                            <a
                                href="/"
                                className={cn(
                                    'inline-flex max-h-[var(--nav-logo-max-h-mobile)] max-w-[var(--nav-logo-max-w-mobile)] shrink-0 items-center justify-start transition-[max-height,max-width] motion-reduce:transition-none lg:max-h-[var(--nav-logo-max-h-lg)] lg:max-w-[var(--nav-logo-max-w-lg)]',
                                    logoResizeTransitionClass,
                                    isExpandedLargeOverflow
                                        ? 'lg:items-start'
                                        : ''
                                )}
                                style={logoBoxStyle}
                            >
                                <NavLogoImage
                                    className={desktopLogoImageClass}
                                    src={resolvedLogoUrl}
                                    alt={resolvedLogoAltText}
                                    width={NAV_LOGO_IMAGE_DIMENSIONS.width}
                                    height={NAV_LOGO_IMAGE_DIMENSIONS.height}
                                    sizes={desktopLogoSizes}
                                />
                            </a>
                        ) : null}
                    </div>

                    {/* CTA + Menu - mobile only */}
                    <div
                        className={cn(
                            'flex items-center gap-2 whitespace-nowrap pr-2 lg:hidden',
                            isSlimNav ? 'py-1' : 'py-2'
                        )}
                    >
                        {/* CTA Mobile Icon only */}
                        {resolvedCTAMobileIconClasses &&
                            (CustomCTALink ? (
                                <Button
                                    asChild
                                    variant="primary"
                                    size="md"
                                    className={cn(
                                        'nav-action-button text-base',
                                        phoneIconButtonBaseClass,
                                        iconButtonMobileSizeClass
                                    )}
                                >
                                    <a
                                        href={CustomCTALink}
                                        onClick={handleCTAClickSmart}
                                        {...buttonLinkTargetProps(
                                            ctaOpenInNewTabEnabled
                                        )}
                                        aria-label={ctaAriaLabel}
                                    >
                                        <IconValue
                                            value={resolvedCTAMobileIconClasses}
                                            className={ctaIconClassMobile}
                                        />
                                    </a>
                                </Button>
                            ) : (
                                <Button
                                    variant="primary"
                                    size="md"
                                    type="button"
                                    onClick={handleCTAClickSmart}
                                    className={cn(
                                        'nav-action-button nj-lead text-base',
                                        phoneIconButtonBaseClass,
                                        iconButtonMobileSizeClass
                                    )}
                                    aria-label={ctaAriaLabel}
                                >
                                    <IconValue
                                        value={resolvedCTAMobileIconClasses}
                                        className={ctaIconClassMobile}
                                    />
                                </Button>
                            ))}

                        {/* Phone button (mobile) */}
                        {Phone && (
                            <Button
                                asChild
                                variant="secondary"
                                size="md"
                                className={cn(
                                    'nav-action-button text-base',
                                    phoneIconButtonBaseClass,
                                    iconButtonMobileSizeClass
                                )}
                                aria-label="Call us"
                                href={`tel:${Phone}`}
                            >
                                <a>
                                    <span className={phoneIconInnerClass}>
                                        <IconValue
                                            value={
                                                resolvedPhoneMobileIconClasses
                                            }
                                            className={phoneIconClassDesktop}
                                        />
                                    </span>
                                </a>
                            </Button>
                        )}

                        {/* Optional Outline button (mobile) */}
                        {OutlineButtonText && (
                            <Button
                                asChild
                                variant="outline"
                                size="sm"
                                iconDefault={resolvedOutlineButtonIcon}
                                iconVisible={resolvedOutlineButtonIconVisible}
                                className="nav-action-button my-1 mr-2 hidden min-h-[55px] min-w-[55px] text-base"
                            >
                                <a
                                    href={OutlineButtonLink || '#'}
                                    onClick={handleOutlineClick}
                                    {...buttonLinkTargetProps(
                                        outlineOpenInNewTabEnabled
                                    )}
                                >
                                    {OutlineButtonText}
                                </a>
                            </Button>
                        )}

                        {/* Hamburger */}
                        <button
                            type="button"
                            className={cn(
                                'relative z-[70] ml-1 inline-flex shrink-0 cursor-pointer items-center justify-center rounded-[var(--shape-radius)] focus:outline-none',
                                navButtonColor,
                                mobileMenuButtonPadding
                            )}
                            onClick={() => setMobileMenuOpen((open) => !open)}
                            aria-expanded={mobileMenuOpen}
                        >
                            <span className="sr-only">
                                {mobileMenuOpen
                                    ? 'Close main menu'
                                    : 'Open main menu'}
                            </span>
                            {mobileMenuOpen ? (
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    className="pointer-events-none block h-6 w-6 opacity-100"
                                    aria-hidden="true"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M6 18 18 6M6 6l12 12"
                                    />
                                </svg>
                            ) : (
                                <Bars3Icon
                                    className="h-6 w-6"
                                    aria-hidden="true"
                                />
                            )}
                        </button>
                    </div>

                    {/* Desktop nav */}
                    <div
                        className={`my-auto hidden ${navTextSize} lg:flex lg:min-w-0 lg:flex-1 lg:items-center`}
                    >
                        <div className={cn('flex min-w-0 flex-1', navAlign)}>
                            <Popover.Group
                                className={`hidden ${navGroupPaddingY} lg:flex lg:items-center lg:gap-x-1`}
                            >
                                {ServicesHeader && (
                                    <Popover className="shrink-0">
                                        {({ open, close }) => (
                                            <>
                                                <DesktopDropdownDismiss
                                                    open={open}
                                                    close={close}
                                                    headerRef={headerRef}
                                                />
                                                <Popover.Button
                                                    data-nav-desktop-dropdown-trigger
                                                    onPointerDown={
                                                        updateDesktopDropdownPosition
                                                    }
                                                    onFocus={
                                                        updateDesktopDropdownPosition
                                                    }
                                                    className={`focus-visible:transparent flex shrink-0 items-center whitespace-nowrap ${navItemPaddingClass} font-semibold focus-visible:outline focus-visible:outline-0 focus-visible:outline-offset-2 ${linkColor} ${linkHoverColor}`}
                                                >
                                                    <MarkDown>
                                                        {ServicesHeader}
                                                    </MarkDown>
                                                    <ChevronDownIcon
                                                        className="ml-2 h-5 w-5 flex-none"
                                                        aria-hidden="true"
                                                    />
                                                </Popover.Button>
                                                {renderDesktopDropdownPanel(
                                                    <div className="p-2">
                                                        {Services?.map(
                                                            (item) => {
                                                                const isHashLink =
                                                                    !!getHashTargetId(
                                                                        item.Anchor
                                                                    );
                                                                const href =
                                                                    getProcessedHref(
                                                                        item.Anchor
                                                                    );
                                                                return (
                                                                    <div
                                                                        key={
                                                                            item.Name
                                                                        }
                                                                        className={`group relative rounded-[min(1.5rem,var(--shape-radius))] px-4 py-3 ${dropdownItemHoverColor}`}
                                                                    >
                                                                        {isHashLink ? (
                                                                            <a
                                                                                href={
                                                                                    href
                                                                                }
                                                                                onClick={(
                                                                                    e
                                                                                ) => {
                                                                                    handleServiceNavigation(
                                                                                        e,
                                                                                        item.Anchor,
                                                                                        false
                                                                                    );
                                                                                    close();
                                                                                }}
                                                                                className={`block whitespace-normal break-words font-semibold ${dropdownItemColor}`}
                                                                            >
                                                                                <MarkDown>
                                                                                    {
                                                                                        item.Name
                                                                                    }
                                                                                </MarkDown>
                                                                                <span className="absolute inset-0" />
                                                                            </a>
                                                                        ) : (
                                                                            <a
                                                                                href={
                                                                                    href
                                                                                }
                                                                                className={`block whitespace-normal break-words font-semibold ${dropdownItemColor}`}
                                                                                onClick={() => {
                                                                                    close();
                                                                                }}
                                                                            >
                                                                                <MarkDown>
                                                                                    {
                                                                                        item.Name
                                                                                    }
                                                                                </MarkDown>
                                                                                <span className="absolute inset-0" />
                                                                            </a>
                                                                        )}
                                                                    </div>
                                                                );
                                                            }
                                                        )}
                                                    </div>,
                                                    cn(
                                                        'w-max min-w-64 max-w-[26rem]',
                                                        desktopDropdownSurfaceClass
                                                    )
                                                )}
                                            </>
                                        )}
                                    </Popover>
                                )}

                                {Dropdown?.map((menu, index) =>
                                    menu.Header ? (
                                        <Popover
                                            key={index}
                                            className="group shrink-0"
                                        >
                                            {({ open, close }) => (
                                                <>
                                                    <DesktopDropdownDismiss
                                                        open={open}
                                                        close={close}
                                                        headerRef={headerRef}
                                                    />
                                                    <Popover.Button
                                                        data-nav-desktop-dropdown-trigger
                                                        onPointerDown={
                                                            updateDesktopDropdownPosition
                                                        }
                                                        onFocus={
                                                            updateDesktopDropdownPosition
                                                        }
                                                        className={`focus-visible:transparent flex shrink-0 items-center whitespace-nowrap ${navItemPaddingClass} font-semibold focus-visible:outline focus-visible:outline-0 focus-visible:outline-offset-2 ${linkColor} ${linkHoverColor}`}
                                                    >
                                                        <MarkDown>
                                                            {menu.Header}
                                                        </MarkDown>
                                                        <ChevronDownIcon
                                                            className="ml-2 h-5 w-5 flex-none"
                                                            aria-hidden="true"
                                                        />
                                                    </Popover.Button>
                                                    {renderDesktopDropdownPanel(
                                                        <div className="p-2">
                                                            {menu.Items?.map(
                                                                (
                                                                    item,
                                                                    itemIndex
                                                                ) => {
                                                                    const href =
                                                                        getProcessedHref(
                                                                            item.Anchor
                                                                        );
                                                                    const isHashLink =
                                                                        !!getHashTargetId(
                                                                            item.Anchor
                                                                        );

                                                                    return (
                                                                        <div
                                                                            key={
                                                                                itemIndex
                                                                            }
                                                                            className={`group relative rounded-[min(1.5rem,var(--shape-radius))] px-4 py-3 ${dropdownItemHoverColor}`}
                                                                        >
                                                                            <a
                                                                                href={
                                                                                    href
                                                                                }
                                                                                onClick={(
                                                                                    e
                                                                                ) => {
                                                                                    if (
                                                                                        isHashLink
                                                                                    ) {
                                                                                        handleServiceNavigation(
                                                                                            e,
                                                                                            item.Anchor,
                                                                                            false
                                                                                        );
                                                                                    }
                                                                                    close();
                                                                                }}
                                                                                className={`block whitespace-normal break-words font-semibold ${dropdownItemColor}`}
                                                                            >
                                                                                <MarkDown>
                                                                                    {
                                                                                        item.Name
                                                                                    }
                                                                                </MarkDown>
                                                                                <span className="absolute inset-0" />
                                                                            </a>
                                                                        </div>
                                                                    );
                                                                }
                                                            )}
                                                        </div>,
                                                        cn(
                                                            'w-max min-w-64 max-w-[26rem]',
                                                            desktopDropdownSurfaceClass
                                                        )
                                                    )}
                                                </>
                                            )}
                                        </Popover>
                                    ) : null
                                )}

                                {Links?.map(({ Name, Link }) => {
                                    const isHashLink = !!getHashTargetId(Link);
                                    const href = getProcessedHref(Link);
                                    return (
                                        <a
                                            key={Link}
                                            href={href}
                                            onClick={(e) => {
                                                if (isHashLink)
                                                    handleServiceNavigation(
                                                        e,
                                                        Link,
                                                        false
                                                    );
                                            }}
                                            className={`focus-visible:transparent shrink-0 whitespace-nowrap ${navItemPaddingClass} font-semibold transition-all ease-in focus-visible:outline focus-visible:outline-0 focus-visible:outline-offset-2 ${linkColor} ${linkHoverColor}`}
                                        >
                                            <MarkDown>{Name}</MarkDown>
                                        </a>
                                    );
                                })}

                                {EndDropdowns?.map((menu, index) => {
                                    const endMenuItems = (
                                        menu.Items || []
                                    ).filter(
                                        (item) =>
                                            String(item?.Name || '').trim() &&
                                            String(item?.Anchor || '').trim()
                                    );
                                    if (!menu.Header) return null;
                                    return (
                                        <Popover
                                            key={`end-${index}`}
                                            className="group shrink-0"
                                        >
                                            {({ open, close }) => (
                                                <>
                                                    <DesktopDropdownDismiss
                                                        open={open}
                                                        close={close}
                                                        headerRef={headerRef}
                                                    />
                                                    <Popover.Button
                                                        data-nav-desktop-dropdown-trigger
                                                        onPointerDown={
                                                            updateDesktopDropdownPosition
                                                        }
                                                        onFocus={
                                                            updateDesktopDropdownPosition
                                                        }
                                                        className={`focus-visible:transparent flex shrink-0 items-center whitespace-nowrap ${navItemPaddingClass} font-semibold focus-visible:outline focus-visible:outline-0 focus-visible:outline-offset-2 ${linkColor} ${linkHoverColor}`}
                                                    >
                                                        <MarkDown>
                                                            {menu.Header}
                                                        </MarkDown>
                                                        <ChevronDownIcon
                                                            className="ml-2 h-5 w-5 flex-none"
                                                            aria-hidden="true"
                                                        />
                                                    </Popover.Button>
                                                    {renderDesktopDropdownPanel(
                                                        <div className="p-2">
                                                            {endMenuItems.map(
                                                                (
                                                                    item,
                                                                    itemIndex
                                                                ) => {
                                                                    const href =
                                                                        getProcessedHref(
                                                                            item.Anchor
                                                                        );
                                                                    const isHashLink =
                                                                        !!getHashTargetId(
                                                                            item.Anchor
                                                                        );

                                                                    return (
                                                                        <div
                                                                            key={
                                                                                itemIndex
                                                                            }
                                                                            className={`group relative rounded-[min(1.5rem,var(--shape-radius))] px-4 py-3 ${dropdownItemHoverColor}`}
                                                                        >
                                                                            <a
                                                                                href={
                                                                                    href
                                                                                }
                                                                                onClick={(
                                                                                    e
                                                                                ) => {
                                                                                    if (
                                                                                        isHashLink
                                                                                    ) {
                                                                                        handleServiceNavigation(
                                                                                            e,
                                                                                            item.Anchor,
                                                                                            false
                                                                                        );
                                                                                    }
                                                                                    close();
                                                                                }}
                                                                                className={`block whitespace-normal break-words font-semibold ${dropdownItemColor}`}
                                                                            >
                                                                                <MarkDown>
                                                                                    {
                                                                                        item.Name
                                                                                    }
                                                                                </MarkDown>
                                                                                <span className="absolute inset-0" />
                                                                            </a>
                                                                        </div>
                                                                    );
                                                                }
                                                            )}
                                                        </div>,
                                                        cn(
                                                            'w-max max-w-[22rem]',
                                                            desktopDropdownSurfaceClass
                                                        )
                                                    )}
                                                </>
                                            )}
                                        </Popover>
                                    );
                                })}
                            </Popover.Group>
                        </div>

                        <div className="flex justify-end gap-2 whitespace-nowrap">
                            {CTA && (
                                <Button
                                    asChild
                                    variant="primary"
                                    size="md"
                                    inlineFieldKey="CTA"
                                    iconDefault={resolvedCTAIcon}
                                    iconVisible={resolvedCTAIconVisible}
                                    className={cn(
                                        'nav-action-button my-auto shrink-0 whitespace-nowrap',
                                        desktopActionClass,
                                        CustomCTALink ? '' : 'nj-lead'
                                    )}
                                    aria-label={ctaAriaLabel}
                                >
                                    <a
                                        href={CustomCTALink || '#'}
                                        onClick={handleCTAClickSmart}
                                        {...buttonLinkTargetProps(
                                            ctaOpenInNewTabEnabled
                                        )}
                                    >
                                        {CTA}
                                    </a>
                                </Button>
                            )}

                            {/* Outline button (desktop) */}
                            {OutlineButtonText && (
                                <Button
                                    asChild
                                    variant={thirdButton}
                                    size="md"
                                    iconDefault={resolvedOutlineButtonIcon}
                                    iconVisible={
                                        resolvedOutlineButtonIconVisible
                                    }
                                    className={cn(
                                        'nav-action-button my-auto hidden lg:block',
                                        desktopActionClass
                                    )}
                                >
                                    <a
                                        href={OutlineButtonLink || '#'}
                                        onClick={handleOutlineClick}
                                        {...buttonLinkTargetProps(
                                            outlineOpenInNewTabEnabled
                                        )}
                                    >
                                        {OutlineButtonText}
                                    </a>
                                </Button>
                            )}

                            {/* Phone (desktop) */}
                            {Phone && (
                                <Button
                                    asChild
                                    variant="secondary"
                                    size="md"
                                    iconDefault={
                                        showPhoneNumber && showPhoneIconDesktop
                                            ? resolvedPhoneIconClasses
                                            : undefined
                                    }
                                    iconVisible={
                                        showPhoneNumber && showPhoneIconDesktop
                                    }
                                    iconPosition={resolvedPhoneIconPosition}
                                    className={cn(
                                        'nav-action-button my-auto lg:block',
                                        desktopPhoneClass,
                                        showPhoneNumber &&
                                            showPhoneIconDesktop &&
                                            '!gap-0',
                                        showPhoneNumber &&
                                            showPhoneIconDesktop &&
                                            resolvedPhoneIconPosition ===
                                                'left' &&
                                            '[&_.button-icon]:!mr-2',
                                        showPhoneNumber &&
                                            showPhoneIconDesktop &&
                                            resolvedPhoneIconPosition ===
                                                'right' &&
                                            '[&_.button-icon]:!ml-2',
                                        showPhoneNumber
                                            ? ''
                                            : cn(
                                                  phoneIconButtonBaseClass,
                                                  phoneIconButtonDesktopSizeClass
                                              )
                                    )}
                                    aria-label="Call us"
                                    href={`tel:${Phone}`}
                                >
                                    <a>
                                        {showPhoneNumber ? (
                                            Phone
                                        ) : (
                                            <span
                                                className={phoneIconInnerClass}
                                            >
                                                <IconValue
                                                    value={
                                                        resolvedPhoneIconClasses
                                                    }
                                                    className={
                                                        phoneIconClassDesktop
                                                    }
                                                />
                                            </span>
                                        )}
                                    </a>
                                </Button>
                            )}
                        </div>
                    </div>
                </nav>

                {/* Mobile menu */}
                {isEditMode || isFloatingNavScheme ? (
                    <Transition show={mobileMenuOpen} as={Fragment}>
                        <div className="lg:hidden">
                            <div
                                className={mobileMenuOverlayClass}
                                onClick={() => setMobileMenuOpen(false)}
                            />
                            <Transition.Child
                                as={Fragment}
                                enter={mobileMenuTransitionEnter}
                                enterFrom={mobileMenuTransitionEnterFrom}
                                enterTo={mobileMenuTransitionEnterTo}
                                leave={mobileMenuTransitionLeave}
                                leaveFrom={mobileMenuTransitionLeaveFrom}
                                leaveTo={mobileMenuTransitionLeaveTo}
                            >
                                <div
                                    className={cn(
                                        mobileMenuPositionClass,
                                        mobileMenuSurfaceClass
                                    )}
                                >
                                    <Transition.Child
                                        as={Fragment}
                                        enter={mobileMenuContentTransitionEnter}
                                        enterFrom={
                                            mobileMenuContentTransitionEnterFrom
                                        }
                                        enterTo={
                                            mobileMenuContentTransitionEnterTo
                                        }
                                        leave={mobileMenuContentTransitionLeave}
                                        leaveFrom={
                                            mobileMenuContentTransitionLeaveFrom
                                        }
                                        leaveTo={
                                            mobileMenuContentTransitionLeaveTo
                                        }
                                    >
                                        <div>{mobileMenuContent}</div>
                                    </Transition.Child>
                                </div>
                            </Transition.Child>
                        </div>
                    </Transition>
                ) : (
                    <Dialog
                        as="div"
                        className="lg:hidden"
                        open={mobileMenuOpen}
                        onClose={() => setMobileMenuOpen(false)}
                    >
                        <Transition
                            show={mobileMenuOpen}
                            as={Fragment}
                            enter={mobileMenuTransitionEnter}
                            enterFrom={mobileMenuTransitionEnterFrom}
                            enterTo={mobileMenuTransitionEnterTo}
                            leave={mobileMenuTransitionLeave}
                            leaveFrom={mobileMenuTransitionLeaveFrom}
                            leaveTo={mobileMenuTransitionLeaveTo}
                        >
                            <Dialog.Panel
                                className={cn(
                                    mobileMenuDialogPositionClass,
                                    mobileMenuSurfaceClass
                                )}
                            >
                                <Transition.Child
                                    as={Fragment}
                                    enter={mobileMenuContentTransitionEnter}
                                    enterFrom={
                                        mobileMenuContentTransitionEnterFrom
                                    }
                                    enterTo={mobileMenuContentTransitionEnterTo}
                                    leave={mobileMenuContentTransitionLeave}
                                    leaveFrom={
                                        mobileMenuContentTransitionLeaveFrom
                                    }
                                    leaveTo={mobileMenuContentTransitionLeaveTo}
                                >
                                    <div>{mobileMenuContent}</div>
                                </Transition.Child>
                            </Dialog.Panel>
                        </Transition>
                        <Transition
                            show={mobileMenuOpen}
                            as={Fragment}
                            enter="transition-opacity ease-linear duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="transition-opacity ease-linear duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <Dialog.Overlay
                                className={mobileMenuOverlayClass}
                            />
                        </Transition>
                    </Dialog>
                )}
            </header>
            {isFixedPosition && (
                <div
                    ref={spacerRef}
                    aria-hidden="true"
                    style={{
                        height: spacerHeight,
                        transition: isRevealBehavior
                            ? 'height 200ms ease'
                            : undefined,
                    }}
                />
            )}

            {/* Shared lightbox for embedded links */}
            <Dialog
                open={embedOpen}
                onClose={() => setEmbedOpen(false)}
                className="relative z-[60]"
            >
                <Transition
                    show={embedOpen}
                    as={Fragment}
                    enter="transition-opacity ease-linear duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition-opacity ease-linear duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
                </Transition>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition
                            show={embedOpen}
                            as={Fragment}
                            enter="transition ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="transition ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="relative w-full max-w-5xl overflow-hidden rounded-[min(1.5rem,var(--shape-radius))] bg-white shadow-xl">
                                <button
                                    type="button"
                                    className="absolute right-3 top-3 inline-flex items-center justify-center rounded-full p-2 text-neutral-700 hover:bg-neutral-100 focus:outline-none"
                                    onClick={() => setEmbedOpen(false)}
                                    aria-label="Close dialog"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                                <div className="h-[80vh] w-full">
                                    {embedSrc ? (
                                        <iframe
                                            src={embedSrc}
                                            title="Embedded content"
                                            className="h-full w-full"
                                            loading="eager"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                                        />
                                    ) : (
                                        <div className="flex h-full items-center justify-center p-8 text-center text-sm text-neutral-600">
                                            No link provided.
                                        </div>
                                    )}
                                </div>
                            </Dialog.Panel>
                        </Transition>
                    </div>
                </div>
            </Dialog>
        </>
    );
}
